import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";

// --- MUI & Toolpad Imports ---
import Box from "@mui/material/Box";
import { createTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { useDemoRouter } from "@toolpad/core/internal";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";

// --- MUI Form & Icon Imports ---
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import Popover from "@mui/material/Popover";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Pagination from "@mui/material/Pagination";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AlarmIcon from "@mui/icons-material/Alarm";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BoltIcon from "@mui/icons-material/Bolt";
import UndoIcon from "@mui/icons-material/Undo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import BrightnessAutoOutlinedIcon from "@mui/icons-material/BrightnessAutoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkIcon from "@mui/icons-material/Work";
import HomeIcon from "@mui/icons-material/Home";

// --- Swipeable List Imports ---
import {
  SwipeableList,
  SwipeableListItem,
  LeadingActions,
  TrailingActions,
  SwipeAction,
  Type as ListType,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";

// --- Action Icons for Swipe & Drag ---
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";

// --- FullCalendar Imports ---
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, {
  Draggable as FcDraggable,
} from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import jaLocale from "@fullcalendar/core/locales/ja";


// --- Firebase Imports ---
import { db, auth, provider } from "./firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";

// --- 共通ユーティリティ ---
const statusToSlug = (status) => {
  const map = {
    未対応: "mitaio",
    対応中: "taiouchu",
    一旦: "ittan",
    完了: "kanryo",
    保留: "horyu",
    連絡待ち: "renrakumachi",
    対応不要: "taiouhuyou",
  };
  return map[status] || String(status).replace(/\s+/g, "-").toLowerCase();
};

const getTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateTime = (str) => {
  if (!str) return { date: getTodayDate(), time: "" };
  if (str.indexOf("T") === -1) return { date: str, time: "" };
  const d = new Date(str);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
};

const toDateTimeStr = (dateObj, isAllDay = false) => {
  if (!dateObj) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  if (isAllDay) return `${year}-${month}-${day}`;
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const STATUS_CONFIG = {
  未対応: {
    bg: "var(--status-mitaio-light)",
    badgeBg: "var(--status-mitaio-badge-bg)",
    text: "var(--status-mitaio-dark)",
    border: "var(--status-mitaio-main)",
    dot: "var(--status-mitaio-dark)",
    dark: "var(--status-mitaio-dark)",
  },
  対応中: {
    bg: "var(--status-taiouchu-light)",
    badgeBg: "var(--status-taiouchu-badge-bg)",
    text: "var(--status-taiouchu-dark)",
    border: "var(--status-taiouchu-main)",
    dot: "var(--status-taiouchu-dark)",
    dark: "var(--status-taiouchu-dark)",
  },
  一旦: {
    bg: "var(--status-ittan-light)",
    badgeBg: "var(--status-ittan-badge-bg)",
    text: "var(--status-ittan-dark)",
    border: "var(--status-ittan-main)",
    dot: "var(--status-ittan-dark)",
    dark: "var(--status-ittan-dark)",
  },
  完了: {
    bg: "var(--status-kanryo-light)",
    badgeBg: "var(--status-kanryo-badge-bg)",
    text: "var(--status-kanryo-dark)",
    border: "var(--status-kanryo-main)",
    dot: "var(--status-kanryo-dark)",
    dark: "var(--status-kanryo-dark)",
  },
  保留: {
    bg: "var(--status-horyu-light)",
    badgeBg: "var(--status-horyu-badge-bg)",
    text: "var(--status-horyu-dark)",
    border: "var(--status-horyu-main)",
    dot: "var(--status-horyu-dark)",
    dark: "var(--status-horyu-dark)",
  },
  連絡待ち: {
    bg: "var(--status-renrakumachi-light)",
    badgeBg: "var(--status-renrakumachi-badge-bg)",
    text: "var(--status-renrakumachi-dark)",
    border: "var(--status-renrakumachi-main)",
    dot: "var(--status-renrakumachi-dark)",
    dark: "var(--status-renrakumachi-dark)",
  },
  対応不要: {
    bg: "var(--status-taiouhuyou-light)",
    badgeBg: "var(--status-taiouhuyou-badge-bg)",
    text: "var(--status-taiouhuyou-dark)",
    border: "var(--status-taiouhuyou-main)",
    dot: "var(--status-taiouhuyou-dark)",
    dark: "var(--status-taiouhuyou-dark)",
  },
};

const formatDateWithWeekday = (str) => {
  if (!str) return "";
  const datePart = str.slice(0, 10);
  const parts = datePart.split("-");
  if (parts.length !== 3) return "";
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const y = parts[0];
  const m = parts[1];
  const day = parts[2];
  const w = weekdays[d.getDay()];
  return `${y}/${m}/${day}(${w})`;
};

const getTaskTimes = (task) => {
  let startTime = "-";
  let endTime = "-";
  if (task.isAllDay) {
    startTime = "終日";
  } else {
    if (task.start && task.start.includes("T")) {
      startTime = parseDateTime(task.start).time;
    }
    if (task.end && task.end.includes("T")) {
      endTime = parseDateTime(task.end).time;
    }
  }
  return { startTime, endTime, duration: `${task.estimatedHours ?? 0}h` };
};

// --- 15分刻みの時間オプション (00:00〜23:45) ---
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = String(Math.floor(i / 4)).padStart(2, "0");
  const m = String((i % 4) * 15).padStart(2, "0");
  return `${h}:${m}`;
});

// --- 統一スマート時間選択ドロップダウン ---
const TimeSelectDropdown = ({
  label,
  value,
  onChange,
  placeholder = "09:00",
  sx = { width: 130 },
  startAdornment = null,
}) => (
  <Autocomplete
    freeSolo
    size="small"
    options={TIME_OPTIONS}
    value={value || ""}
    onChange={(event, newValue) => {
      onChange(newValue || "");
    }}
    onInputChange={(event, newInputValue) => {
      onChange(newInputValue || "");
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          ...params.InputProps,
          ...(startAdornment ? { startAdornment } : {}),
        }}
      />
    )}
    sx={sx}
  />
);

// --- 精密秒同期 Mac風ヘッダークロック ---
const HeaderClock = () => {
  const [timeStr, setTimeStr] = useState(() => {
    const d = new Date();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const w = weekdays[d.getDay()];
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${m}月${day}日(${w}) ${hh}:${mm}:${ss}`;
  });

  useEffect(() => {
    let timerId;
    const update = () => {
      const d = new Date();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const w = weekdays[d.getDay()];
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      setTimeStr(`${m}月${day}日(${w}) ${hh}:${mm}:${ss}`);

      // 次の秒の変わり目（ジャスト0ミリ秒）を正確に計算して自己補正同期
      const delay = 1000 - d.getMilliseconds();
      timerId = setTimeout(update, delay);
    };

    const initialDelay = 1000 - new Date().getMilliseconds();
    timerId = setTimeout(update, initialDelay);

    return () => clearTimeout(timerId);
  }, []);

  return (
    <Box
      sx={{
        display: { xs: "none", sm: "inline-flex" },
        alignItems: "center",
        px: 0.8,
        color: "text.secondary",
        fontSize: "0.82rem",
        fontWeight: 500,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Mono", Menlo, Consolas, monospace',
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.2px",
        userSelect: "none",
      }}
    >
      {timeStr}
    </Box>
  );
};

// --- 時報トースト通知（右上からスッと出て1.5秒で消える） ---
const TimeChimeToast = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        top: 76,
        right: 24,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        px: 2,
        py: 1,
        borderRadius: "24px",
        bgcolor: "#1e293b",
        color: "#f8fafc",
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        cursor: "pointer",
        backdropFilter: "blur(12px)",
        animation: "chimeSlideDown 0.32s cubic-bezier(0.16, 1, 0.3, 1) both",
        willChange: "transform, opacity",
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: "rgba(59, 130, 246, 0.2)",
          color: "#60a5fa",
        }}
      >
        <NotificationsActiveIcon sx={{ fontSize: 15 }} />
      </Box>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 600,
          letterSpacing: "0.2px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};
const TomatoIcon = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{
      display: "inline-block",
      verticalAlign: "-1.5px",
      marginRight: 3,
    }}
  >
    <circle cx="12" cy="13" r="8.5" fill="#ef4444" />
    <path
      d="M12 4.5 C12 3 13 2 14 2"
      stroke="#15803d"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M12 5.5 C10 4 7 5 7 6 C9 6.5 11 6.5 12 7 C13 6.5 15 6.5 17 6 C17 5 14 4 12 5.5 Z"
      fill="#22c55e"
    />
  </svg>
);

// --- Quick Duration Picker Component ---
const QuickDurationPicker = ({
  durationMinutes,
  onChangeDuration,
  startTime,
  endTime,
}) => {
  const clampMinutes = Math.max(0, durationMinutes || 0);

  const QUICK_DURATIONS = [
    { label: "15m", mins: 15 },
    { label: "25m", mins: 25, icon: <TomatoIcon size={13} /> },
    { label: "30m", mins: 30 },
    { label: "45m", mins: 45 },
    { label: "1h", mins: 60 },
    { label: "2h", mins: 120 },
    { label: "4h", mins: 240 },
    { label: "8h", mins: 480 },
  ];

  return (
    <Box
      sx={{
        bgcolor: "action.hover",
        borderRadius: 2,
        p: 1.5,
        mt: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          所要時間
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, color: "primary.main", fontSize: "0.85rem" }}
        >
          {clampMinutes > 0
            ? clampMinutes >= 60
              ? `${(clampMinutes / 60).toFixed(1)}h`
              : `${clampMinutes}m`
            : "--"}
        </Typography>
      </Box>

      {/* Quick Duration Pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
        {QUICK_DURATIONS.map(({ label, mins, icon }) => {
          const isSelected = clampMinutes === mins;
          return (
            <Button
              key={mins}
              size="small"
              onClick={() => onChangeDuration(mins)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "26px",
                p: 0,
                borderRadius: "14px",
                fontSize: "0.75rem",
                fontWeight: 600,
                minWidth: mins === 25 ? 56 : 42,
                height: 28,
                textTransform: "none",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",
                bgcolor: isSelected ? "primary.main" : "background.paper",
                color: isSelected ? "#fff" : "text.primary",
                boxShadow: isSelected
                  ? "0 2px 6px rgba(59, 130, 246, 0.3)"
                  : "none",
                transition:
                  "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: isSelected
                    ? "primary.dark"
                    : "action.selected",
                },
              }}
            >
              {icon}
              {label}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

// --- Task Edit Modal (MUI Dialog) ---
const TaskModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  defaultValues = {},
  availableCategories = [],
  onAddNewCategory,
}) => {
  const isEditing = !!defaultValues.id;
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("仕事");
  const [status, setStatus] = useState("未対応");
  const [memo, setMemo] = useState("");
  const [isAddingNewCat, setIsAddingNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [date, setDate] = useState(getTodayDate());
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(30);

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultValues.title || "");
      setCategory(defaultValues.category || "work");
      setStatus(defaultValues.status || "未対応");
      setMemo(defaultValues.memo || "");
      setIsAllDay(
        defaultValues.isAllDay !== undefined ? defaultValues.isAllDay : false,
      );

      let d = getTodayDate();
      let st = "";
      let et = "";

      if (defaultValues.start) {
        const parsed = parseDateTime(defaultValues.start);
        d = parsed.date;
        st = parsed.time;
      }
      if (defaultValues.end) {
        const parsed = parseDateTime(defaultValues.end);
        et = parsed.time;
      }

      setDate(d);
      setStartTime(st);
      setEndTime(et);

      if (st && et) {
        const sDate = new Date(`${d}T${st}`);
        const eDate = new Date(`${d}T${et}`);
        const diffM = Math.round(
          (eDate.getTime() - sDate.getTime()) / (1000 * 60),
        );
        if (diffM > 0) setDurationMinutes(diffM);
      } else if (
        defaultValues.estimatedHours &&
        defaultValues.estimatedHours > 0
      ) {
        setDurationMinutes(Math.round(defaultValues.estimatedHours * 60));
      } else {
        setDurationMinutes(30);
      }
    }
  }, [isOpen, defaultValues]);

  const handleSetNow = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const nowStr = `${hh}:${mm}`;
    setDate(toDateTimeStr(now, true));
    handleStartTimeChange(nowStr);
  };

  const handleClearTime = () => {
    setDate("");
    setStartTime("");
    setEndTime("");
    setIsAllDay(false);
  };

  const handleShiftStart = (diffMins) => {
    const currentDate = date || getTodayDate();
    let currentStart = startTime;
    if (!currentStart) {
      const now = new Date();
      currentStart = toDateTimeStr(now, false).slice(11, 16);
    }
    const sDate = new Date(`${currentDate}T${currentStart}`);
    const newSDate = new Date(sDate.getTime() + diffMins * 60 * 1000);
    const newStartStr = toDateTimeStr(newSDate, false).slice(11, 16);
    handleStartTimeChange(newStartStr);
  };

  const handleDurationChange = (mins) => {
    setDurationMinutes(mins);
    if (startTime && mins > 0) {
      const parts = startTime.split(":");
      if (parts.length === 2) {
        const sHours = Number(parts[0]);
        const sMins = Number(parts[1]);
        const totalStartMins = sHours * 60 + sMins;
        const totalEndMins = totalStartMins + mins;
        const eHours = Math.floor(totalEndMins / 60) % 24;
        const eMins = totalEndMins % 60;
        setEndTime(
          `${String(eHours).padStart(2, "0")}:${String(eMins).padStart(2, "0")}`,
        );
      }
    }
  };

  const handleStartTimeChange = (val) => {
    setStartTime(val);
    if (val && durationMinutes > 0) {
      const parts = val.split(":");
      if (parts.length === 2) {
        const sHours = Number(parts[0]);
        const sMins = Number(parts[1]);
        const totalStartMins = sHours * 60 + sMins;
        const totalEndMins = totalStartMins + durationMinutes;
        const eHours = Math.floor(totalEndMins / 60) % 24;
        const eMins = totalEndMins % 60;
        setEndTime(
          `${String(eHours).padStart(2, "0")}:${String(eMins).padStart(2, "0")}`,
        );
      }
    }
  };

  const handleEndTimeChange = (newEndTime) => {
    setEndTime(newEndTime);
    if (startTime && newEndTime) {
      const currentDate = date || getTodayDate();
      const sDate = new Date(`${currentDate}T${startTime}`);
      const eDate = new Date(`${currentDate}T${newEndTime}`);
      const diffM = Math.round(
        (eDate.getTime() - sDate.getTime()) / (1000 * 60),
      );
      if (diffM > 0) {
        setDurationMinutes(diffM);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    let finalStart = null;
    let finalEnd = null;
    if (date) {
      if (isAllDay) {
        finalStart = date;
      } else {
        finalStart = startTime ? `${date}T${startTime}` : null;
        finalEnd = endTime ? `${date}T${endTime}` : null;
      }
    }
    const finalEstimatedHours =
      durationMinutes > 0 ? Number((durationMinutes / 60).toFixed(2)) : 0;
    onSubmit({
      title,
      category,
      status,
      start: finalStart,
      end: finalEnd,
      isAllDay,
      estimatedHours: finalEstimatedHours,
      memo,
    });
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          backgroundImage: "none",
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 2.2,
            pb: 1,
            px: 2.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight="700">
            {isEditing ? "タスクの編集" : "新しいタスク"}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 1, pb: 2 }}>
          <Stack spacing={1.6}>
            {/* タスク名入力 */}
            <TextField
              size="small"
              placeholder="タスク名を入力"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              autoFocus
              variant="outlined"
            />

            {/* カテゴリ & ステータス（2カラムでスッキリ配置） */}
            <Stack direction="row" spacing={1.2}>
              {/* カテゴリ選択 */}
              <Box sx={{ flex: 1 }}>
                {isAddingNewCat ? (
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <TextField
                      autoFocus
                      size="small"
                      placeholder="新規カテゴリ名"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = newCatName.trim();
                          if (trimmed) {
                            if (onAddNewCategory) onAddNewCategory(trimmed);
                            setCategory(trimmed);
                            setNewCatName("");
                            setIsAddingNewCat(false);
                          }
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const trimmed = newCatName.trim();
                        if (trimmed) {
                          if (onAddNewCategory) onAddNewCategory(trimmed);
                          setCategory(trimmed);
                          setNewCatName("");
                          setIsAddingNewCat(false);
                        }
                      }}
                      disabled={!newCatName.trim()}
                      sx={{ minWidth: 40, px: 1, textTransform: "none", fontWeight: "bold" }}
                    >
                      追加
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setIsAddingNewCat(false);
                        setNewCatName("");
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <TextField
                    select
                    size="small"
                    label="カテゴリ"
                    value={category || ""}
                    onChange={(e) => {
                      if (e.target.value === "__NEW__") {
                        setIsAddingNewCat(true);
                      } else {
                        setCategory(e.target.value);
                      }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) {
                          return <span style={{ color: "#94a3b8" }}>未設定</span>;
                        }
                        return selected;
                      },
                    }}
                    fullWidth
                  >
                    <MenuItem value="">
                      <span style={{ color: "#94a3b8" }}>未設定</span>
                    </MenuItem>
                    {(availableCategories || []).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                    <MenuItem
                      value="__NEW__"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        mt: 0.5,
                      }}
                    >
                      ＋ 新規追加...
                    </MenuItem>
                  </TextField>
                )}
              </Box>

              {/* ステータス選択 */}
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  size="small"
                  label="ステータス"
                  value={status || "未対応"}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                >
                  {[
                    "未対応",
                    "対応中",
                    "一旦",
                    "完了",
                    "保留",
                    "連絡待ち",
                    "対応不要",
                  ].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            {/* 所要時間クイック選択 */}
            <QuickDurationPicker
              durationMinutes={durationMinutes}
              onChangeDuration={handleDurationChange}
              startTime={startTime}
              endTime={endTime}
            />

            {/* 日付・時間設定ボックス */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 1.5,
                bgcolor: "action.hover",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.75rem" }}
                >
                  日付・時間
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isAllDay}
                      onChange={(e) => setIsAllDay(e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={<Typography variant="caption" fontWeight="600">終日</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>

              <TextField
                type="date"
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                sx={{ mb: isAllDay ? 0 : 1.2 }}
              />

              {!isAllDay && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={TIME_OPTIONS}
                      value={startTime || ""}
                      onChange={(event, newValue) => {
                        handleStartTimeChange(newValue || "");
                      }}
                      onInputChange={(event, newInputValue) => {
                        handleStartTimeChange(newInputValue || "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="09:00"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start" sx={{ mr: 0.3 }}>
                                  <Tooltip title="現在時刻" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSetNow();
                                      }}
                                      sx={{
                                        p: 0.3,
                                        color: "primary.main",
                                        bgcolor: "rgba(59, 130, 246, 0.08)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />

                    <Typography
                      color="text.secondary"
                      sx={{ fontWeight: "bold", opacity: 0.5, fontSize: "0.85rem" }}
                    >
                      〜
                    </Typography>

                    <Autocomplete
                      freeSolo
                      size="small"
                      options={TIME_OPTIONS}
                      value={endTime || ""}
                      onChange={(event, newValue) => {
                        handleEndTimeChange(newValue || "");
                      }}
                      onInputChange={(event, newInputValue) => {
                        handleEndTimeChange(newInputValue || "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="10:00" />
                      )}
                      sx={{ flex: 1 }}
                    />
                  </Stack>

                  {/* 開始時刻のクイックシフト (-15m, +15m) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      mt: 1,
                    }}
                  >
                    {[
                      { label: "-15m", mins: -15 },
                      { label: "+15m", mins: 15 },
                    ].map(({ label, mins }) => (
                      <Button
                        key={label}
                        size="small"
                        onClick={() => handleShiftStart(mins)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                          height: 22,
                          minWidth: 42,
                          borderRadius: "11px",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          textTransform: "none",
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          color: "text.secondary",
                          "&:hover": {
                            borderColor: "primary.main",
                            color: "primary.main",
                          },
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            {/* メモ入力 */}
            <TextField
              size="small"
              placeholder="メモを入力（任意）"
              multiline
              rows={2}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>

        {/* フッターアクション（スクロールなしで常時表示） */}
        <DialogActions
          sx={{
            py: 1.5,
            px: 2.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          {isEditing && onDelete && (
            <Button
              size="small"
              onClick={() => {
                onDelete(defaultValues.id);
                onClose();
              }}
              color="error"
              variant="text"
              sx={{ mr: "auto", fontWeight: 600, textTransform: "none" }}
            >
              削除
            </Button>
          )}
          <Button
            size="small"
            onClick={onClose}
            color="inherit"
            sx={{ fontWeight: 600, textTransform: "none" }}
          >
            キャンセル
          </Button>
          <Button
            size="small"
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              fontWeight: 700,
              textTransform: "none",
              px: 2.2,
              borderRadius: 1.5,
            }}
          >
            {isEditing ? "保存する" : "作成する"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// --- TaskItemCard Component (Dribbble-Style Card) ---
const TaskItemCard = ({
  task,
  setEditingTask,
  setIsModalOpen,
  editTask,
  isLinkedHovered,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const statusStyle = STATUS_CONFIG[task.status] || STATUS_CONFIG["未対応"];
  const { startTime, endTime, duration } = getTaskTimes(task);
  const formattedDate = formatDateWithWeekday(task.start);
  const hasTime = task.start && !task.isAllDay && task.start.includes("T");

  const handleStatusClick = (e) => {
    e.stopPropagation();
    setStatusMenuAnchor(e.currentTarget);
  };

  const handleStatusSelect = (newStatus, e) => {
    e.stopPropagation();
    setStatusMenuAnchor(null);
    if (editTask && task.id) {
      editTask(task.id, { status: newStatus });
    }
  };

  return (
    <div
      className={`task-item status-${statusToSlug(
        task.status,
      )} status-${task.status} ${hasTime ? "has-time" : ""} ${
        isLinkedHovered ? "is-linked-hovered" : ""
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className="inner"
        style={{
          backgroundColor: statusStyle.bg,
          borderColor: statusStyle.border,
        }}
        onClick={() => {
          setEditingTask(task);
          setIsModalOpen(true);
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            width: "100%",
            minHeight: "48px",
          }}
        >
          {/* 左：ステータス（上下中央）＆ 日付（下部） */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexShrink: 0,
              width: "82px",
            }}
          >
            {/* ステータスバッジ */}
            <Box
              onClick={handleStatusClick}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "7px",
                py: "2px",
                borderRadius: "12px",
                fontSize: "0.68rem",
                fontWeight: 600,
                lineHeight: 1.2,
                bgcolor: statusStyle.badgeBg,
                color: statusStyle.text,
                border: `1px solid ${statusStyle.border}`,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                transition: "all 0.15s ease",
                "&:hover": {
                  filter: "brightness(0.95)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: statusStyle.dot,
                  flexShrink: 0,
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {task.status}
              </span>
              <ArrowDropDownIcon
                sx={{ fontSize: 14, ml: -0.4, opacity: 0.7 }}
              />
            </Box>

            {/* 日付（下部・設定されている場合のみ表示） */}
            {task.start && (
              <Typography
                component="div"
                sx={{
                  fontSize: "0.65rem",
                  color: "var(--card-text-secondary, #64748b)",
                  fontWeight: 600,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
                  whiteSpace: "nowrap",
                  letterSpacing: "-0.3px",
                  mt: 0.4,
                }}
              >
                {formattedDate}
              </Typography>
            )}
          </Box>

          {/* 中央：タスク名 ＆ メモ */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              py: 0.2,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "var(--card-text-primary, #0f172a)",
                lineHeight: 1.35,
                wordBreak: "break-word",
                textDecoration:
                  task.status === "対応不要" ? "line-through" : "none",
                opacity: task.status === "対応不要" ? 0.6 : 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {task.title}
            </Typography>
            {task.memo && (
              <Typography
                component="div"
                sx={{
                  fontSize: "0.72rem",
                  color: "var(--card-text-secondary, #64748b)",
                  mt: 0.3,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {task.memo}
              </Typography>
            )}
          </Box>

          {/* 右：開始時刻、終了時刻、工数 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "center",
              flexShrink: 0,
              gap: "2px",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              minWidth: "40px",
              py: 0.2,
            }}
          >
            {/* 開始時刻 */}
            <Typography
              component="div"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                color:
                  startTime !== "-"
                    ? "var(--card-text-primary, #0f172a)"
                    : "var(--card-text-muted, #94a3b8)",
                lineHeight: 1.2,
              }}
            >
              {startTime}
            </Typography>

            {/* 終了時刻 */}
            <Typography
              component="div"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                color:
                  endTime !== "-"
                    ? "var(--card-text-primary, #0f172a)"
                    : "var(--card-text-muted, #94a3b8)",
                lineHeight: 1.2,
              }}
            >
              {endTime}
            </Typography>

            {/* 工数 */}
            <Typography
              component="div"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "var(--card-text-primary, #0f172a)",
                lineHeight: 1.2,
              }}
            >
              {duration}
            </Typography>
          </Box>
        </Box>
      </div>

      {/* ステータス変更ポップオーバーメニュー */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          setStatusMenuAnchor(null);
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            bgcolor: "var(--card-bg-default, #ffffff)",
            border: "1px solid var(--card-border-default, #e2e8f0)",
            p: 0.5,
            minWidth: "120px",
          },
        }}
      >
        {[
          "未対応",
          "対応中",
          "一旦",
          "完了",
          "保留",
          "連絡待ち",
          "対応不要",
        ].map((statusOption) => {
          const optConfig = STATUS_CONFIG[statusOption];
          const isSelected = task.status === statusOption;
          return (
            <MenuItem
              key={statusOption}
              onClick={(e) => handleStatusSelect(statusOption, e)}
              sx={{
                borderRadius: "6px",
                fontSize: "0.75rem",
                fontWeight: isSelected ? 700 : 500,
                py: 0.6,
                px: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: isSelected ? optConfig.bg : "transparent",
                color: optConfig.text,
                "&:hover": {
                  bgcolor: optConfig.bg,
                },
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: optConfig.dot,
                }}
              />
              {statusOption}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

// --- TaskList Component (日付昇順表示・スワイプ対応) ---
const TaskList = ({
  tasks,
  setEditingTask,
  setIsModalOpen,
  editTask,
  draggableElRef,
  isMobile,
  deleteTask,
  hoveredTaskId,
  setHoveredTaskId,
}) => {
  // カレンダー側でホバーされたタスクが画面外にある場合、スムーズにスクロール
  useEffect(() => {
    if (!hoveredTaskId || !draggableElRef?.current) return;
    const itemEl = draggableElRef.current.querySelector(
      `[data-id="${hoveredTaskId}"]`,
    );
    if (itemEl) {
      itemEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [hoveredTaskId, draggableElRef]);

  // 右スワイプ（左側から出現）: 完了
  const leadingActions = (task) => (
    <LeadingActions>
      <SwipeAction
        onClick={() => editTask(task.id, { status: "完了" })}
        destructive={false}
      >
        <Box
          sx={{
            bgcolor: "#4caf50",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <CheckIcon />
            <span style={{ fontSize: "0.7rem", fontWeight: "bold" }}>完了</span>
          </Box>
        </Box>
      </SwipeAction>
    </LeadingActions>
  );

  // 左スワイプ（右側から出現）: 編集・削除
  const trailingActions = (task) => (
    <TrailingActions>
      <SwipeAction
        onClick={() => {
          setEditingTask(task);
          setIsModalOpen(true);
        }}
      >
        <Box
          sx={{
            bgcolor: "#2196f3",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <EditIcon />
        </Box>
      </SwipeAction>
      <SwipeAction destructive={true} onClick={() => deleteTask(task.id)}>
        <Box
          sx={{
            bgcolor: "#f44336",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <DeleteIcon />
        </Box>
      </SwipeAction>
    </TrailingActions>
  );

  return (
    <ul className="task-list" ref={draggableElRef}>
      {tasks.map((task, index) => {
        const content = (
          <TaskItemCard
            task={task}
            setEditingTask={setEditingTask}
            setIsModalOpen={setIsModalOpen}
            editTask={editTask}
            isLinkedHovered={hoveredTaskId === task.id}
            onMouseEnter={() => setHoveredTaskId && setHoveredTaskId(task.id)}
            onMouseLeave={() => setHoveredTaskId && setHoveredTaskId(null)}
          />
        );

        return (
          <li
            key={task.id}
            style={{
              marginBottom: isMobile ? 0 : 5,
              "--stagger-index": Math.min(index, 16),
            }}
            className="fc-draggable-event task-item-stagger"
            data-id={task.id}
            data-title={task.title}
            data-hours={task.estimatedHours || 1}
            data-duration-minutes={
              task.estimatedHours ? Math.round(task.estimatedHours * 60) : 60
            }
          >
            {isMobile ? (
              <SwipeableList
                fullSwipe={false}
                type={ListType.IOS}
                threshold={0.01}
              >
                <SwipeableListItem
                  leadingActions={leadingActions(task)}
                  trailingActions={trailingActions(task)}
                >
                  <div style={{ width: "100%" }}>{content}</div>
                </SwipeableListItem>
              </SwipeableList>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
};

// --- 洗練されたミニマル・スライディング下線カテゴリタブ ---
const CategorySlideTabs = ({
  tabs = [],
  activeTab,
  onTabChange,
  onOpenPricing,
}) => {
  const containerRef = useRef(null);
  const tabRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });

  useLayoutEffect(() => {
    const updateIndicator = () => {
      const activeEl = tabRefs.current[activeTab] || tabRefs.current["all"];
      const containerEl = containerRef.current;
      if (activeEl && containerEl) {
        const activeRect = activeEl.getBoundingClientRect();
        const containerRect = containerEl.getBoundingClientRect();
        const left = activeRect.left - containerRect.left;
        const width = activeRect.width;
        setIndicatorStyle({ left, width, opacity: 1 });
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    const timer = setTimeout(updateIndicator, 50);
    return () => {
      window.removeEventListener("resize", updateIndicator);
      clearTimeout(timer);
    };
  }, [activeTab, tabs]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 3,
        px: 1.5,
        mb: 1.2,
        borderBottom: "1px solid",
        borderColor: "divider",
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {tabs.map((tab) => {
        const isSelected = activeTab === tab.id;
        return (
          <Box
            key={tab.id}
            ref={(el) => {
              if (el) tabRefs.current[tab.id] = el;
            }}
            component="button"
            onClick={() => {
              if (tab.isLocked && onOpenPricing) {
                onOpenPricing();
              } else {
                onTabChange(tab.id);
              }
            }}
            sx={{
              background: "none",
              border: "none",
              outline: "none",
              cursor: "pointer",
              p: 0,
              pt: 0.5,
              pb: 0.8,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: isSelected ? "text.primary" : "text.secondary",
              whiteSpace: "nowrap",
              transition: "color 0.2s ease, opacity 0.2s ease",
              opacity: tab.isLocked ? 0.7 : 1,
              "&:hover": {
                color: tab.isLocked ? "primary.main" : "text.primary",
                opacity: 1,
              },
            }}
          >
            {tab.label}
          </Box>
        );
      })}

      {/* スルッと動くアクティブインジケーター（下線の真上に1pxの隙間なく配置） */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-1px",
          left: 0,
          height: "2px",
          bgcolor: "text.primary",
          borderRadius: "1px",
          width: `${indicatorStyle.width}px`,
          transform: `translateX(${indicatorStyle.left}px)`,
          opacity: indicatorStyle.opacity,
          transition:
            "transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

// --- Page 1: ToDaaay ---
const ToDaaayPage = ({
  tasks,
  holidays,
  addTask,
  editTask,
  deleteTask,
  setEditingTask,
  setIsModalOpen,
  timeSlots,
  firstDayOfWeek,
  calendarTimeMode,
  setCalendarTimeMode,
  focusHours,
  businessMinTime,
  businessMaxTime,
  dailyMinTime,
  dailyMaxTime,
  customMinTime,
  customMaxTime,
  selectedCategory,
  setSelectedCategory,
  availableCategories = [],
  userPlan = "free",
  onOpenPricing,
}) => {
  const [timeSlotAnchorEl, setTimeSlotAnchorEl] = useState(null);

  const timeSlotLabel = useMemo(() => {
    switch (calendarTimeMode) {
      case "business":
        return `${businessMinTime || "09:00"} - ${businessMaxTime || "19:00"}`;
      case "daily":
        return `${dailyMinTime || "07:00"} - ${dailyMaxTime || "23:00"}`;
      case "focus":
        return `今 ±${focusHours}h`;
      case "custom":
        return `${customMinTime} - ${customMaxTime}`;
      case "all":
        return "24時間";
      default:
        return "時間帯";
    }
  }, [
    calendarTimeMode,
    businessMinTime,
    businessMaxTime,
    dailyMinTime,
    dailyMaxTime,
    focusHours,
    customMinTime,
    customMaxTime,
  ]);
  const [taskWidth, setTaskWidth] = useState(() => {
    const saved = localStorage.getItem("taskWidth");
    return saved ? Number(saved) : 320;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("activeTab") || "calendar";
  });
  const [isComposing, setIsComposing] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState("");

  const DEFAULT_FILTER_STATUS = {
    未対応: true,
    対応中: true,
    一旦: true,
    完了: true,
    保留: true,
    連絡待ち: true,
    対応不要: true,
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState(() => {
    const saved = localStorage.getItem("filterStatus");
    if (saved) {
      try {
        return { ...DEFAULT_FILTER_STATUS, ...JSON.parse(saved) };
      } catch (e) {
        // ignore
      }
    }
    return DEFAULT_FILTER_STATUS;
  });

  const [filterTitle, setFilterTitle] = useState(() => {
    return localStorage.getItem("filterTitle") || "";
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("filterTitle") || "";
  });
  const isComposingRef = useRef(false);

  const [currentViewRange, setCurrentViewRange] = useState({
    start: new Date(),
    end: new Date(),
  });

  const [hoveredTaskId, setHoveredTaskId] = useState(null);

  const hoveredTaskDate = useMemo(() => {
    if (!hoveredTaskId) return null;
    const t = tasks.find((task) => task.id === hoveredTaskId);
    if (!t || !t.start) return null;
    return t.start.slice(0, 10);
  }, [hoveredTaskId, tasks]);

  const savedCalendarView = useMemo(() => {
    return localStorage.getItem("fc_currentView") || "timeGridDay";
  }, []);

  const savedCalendarDate = useMemo(() => {
    const saved = localStorage.getItem("fc_currentDate");
    return saved ? new Date(saved) : undefined;
  }, []);

  const calendarRef = useRef(null);
  const draggableElRef = useRef(null);
  const statusOptions = Object.keys(filterStatus);

  useEffect(() => {
    localStorage.setItem("taskWidth", taskWidth);
  }, [taskWidth]);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
    if (activeTab === "calendar" && calendarRef.current) {
      setTimeout(() => {
        calendarRef.current?.getApi()?.updateSize();
      }, 50);
    }
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem("filterStatus", JSON.stringify(filterStatus));
  }, [filterStatus]);

  useEffect(() => {
    localStorage.setItem("filterTitle", searchQuery);
  }, [searchQuery]);

  // タスクカードをホバーした際、カレンダー側のイベントが画面外にあればスムーズスクロール
  useEffect(() => {
    if (!hoveredTaskId || !calendarRef.current) return;
    const calendarEl = calendarRef.current.getApi()?.el;
    if (!calendarEl) return;

    const timer = setTimeout(() => {
      const eventEl = calendarEl.querySelector(".fc-event.is-linked-hovered");
      if (eventEl) {
        eventEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }, 40);

    return () => clearTimeout(timer);
  }, [hoveredTaskId]);

  useEffect(() => {
    const updateViewport = () => {
      const meta = document.querySelector("meta[name='viewport']");
      if (!meta) return;
      const minWidth = 550;
      if (window.outerWidth < minWidth) {
        meta.setAttribute("content", `width=${minWidth}, user-scalable=no`);
      } else {
        meta.setAttribute("content", "width=device-width, initial-scale=1");
      }
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let draggable = null;
    if (draggableElRef.current) {
      draggable = new FcDraggable(draggableElRef.current, {
        itemSelector: ".fc-draggable-event",
        eventData: function (eventEl) {
          const rawMins = eventEl.getAttribute("data-duration-minutes");
          const rawHours = eventEl.getAttribute("data-hours");
          let mins = 60;
          if (rawMins && Number(rawMins) > 0) {
            mins = Number(rawMins);
          } else if (rawHours && Number(rawHours) > 0) {
            mins = Math.round(Number(rawHours) * 60);
          }
          return {
            id: eventEl.getAttribute("data-id"),
            title: eventEl.getAttribute("data-title"),
            duration: { minutes: mins },
            create: true,
          };
        },
      });
    }
    return () => {
      if (draggable) draggable.destroy();
    };
  }, [tasks, isMobile, activeTab, filterStatus, searchQuery]);

  const startResizing = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = taskWidth;
    const onMouseMove = (e) => {
      const newWidth = startWidth - (e.clientX - startX);
      if (newWidth > 100 && newWidth < 800) setTaskWidth(newWidth);
    };
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleEventReceive = (info) => {
    const id = info.event.id;
    const task = tasks.find((t) => t.id === id);
    const isAllDay = info.event.allDay;
    const start = toDateTimeStr(info.event.start, isAllDay);
    let end = info.event.end ? toDateTimeStr(info.event.end, isAllDay) : null;
    if (!isAllDay && !end && info.event.start) {
      const durMins = task?.estimatedHours
        ? Math.round(task.estimatedHours * 60)
        : 60;
      const endDate = new Date(
        info.event.start.getTime() + durMins * 60 * 1000,
      );
      end = toDateTimeStr(endDate, false);
    }

    const oldStart = task ? task.start : null;
    const oldEnd = task ? task.end : null;
    const oldIsAllDay = task ? task.isAllDay : false;

    info.event.remove();
    editTask(id, { start, end, isAllDay });

    setUndoState({
      open: true,
      taskId: id,
      taskTitle: task?.title || info.event.title || "タスク",
      oldStart,
      oldEnd,
      oldIsAllDay,
      revertFn: null,
    });
  };

  const [undoState, setUndoState] = useState({
    open: false,
    taskId: null,
    taskTitle: "",
    oldStart: null,
    oldEnd: null,
    oldIsAllDay: false,
    revertFn: null,
  });

  const handleUndo = () => {
    if (!undoState.taskId) return;
    editTask(undoState.taskId, {
      start: undoState.oldStart,
      end: undoState.oldEnd,
      isAllDay: undoState.oldIsAllDay,
    });
    if (undoState.revertFn) {
      try {
        undoState.revertFn();
      } catch (err) {
        console.error("Revert error", err);
      }
    }
    setUndoState((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        if (undoState.open) {
          e.preventDefault();
          handleUndo();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undoState]);

  const handleEventChange = (info) => {
    const oldIsAllDay = info.oldEvent ? info.oldEvent.allDay : false;
    const oldStart =
      info.oldEvent && info.oldEvent.start
        ? toDateTimeStr(info.oldEvent.start, oldIsAllDay)
        : null;
    const oldEnd =
      info.oldEvent && info.oldEvent.end
        ? toDateTimeStr(info.oldEvent.end, oldIsAllDay)
        : null;

    const isAllDay = info.event.allDay;
    const start = toDateTimeStr(info.event.start, isAllDay);
    const end = info.event.end ? toDateTimeStr(info.event.end, isAllDay) : null;
    editTask(info.event.id, { start, end, isAllDay });

    setUndoState({
      open: true,
      taskId: info.event.id,
      taskTitle: info.event.title || "タスク",
      oldStart,
      oldEnd,
      oldIsAllDay,
      revertFn: () => info.revert(),
    });
  };

  const { filteredTasks } = useMemo(() => {
    const isCategoryFilterActive =
      availableCategories &&
      availableCategories.length >= 2 &&
      selectedCategory &&
      selectedCategory !== "all";

    const result = tasks.filter((t) => {
      if (!filterStatus[t.status]) return false;
      if (
        searchQuery &&
        !t.title.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }
      if (isCategoryFilterActive) {
        if (t.category !== selectedCategory) return false;
      }
      return true;
    });
    const inbox = [];
    const inView = [];
    result.forEach((t) => {
      if (!t.start) {
        inbox.push(t);
      } else {
        const d = new Date(t.start);
        if (d >= currentViewRange.start && d < currentViewRange.end) {
          inView.push(t);
        }
      }
    });
    // 日付・時間昇順でソート（日付未定 -> 終日 -> 時間昇順）
    inView.sort((a, b) => {
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return (a.created?.seconds || 0) - (b.created?.seconds || 0);
    });

    inbox.sort((a, b) => {
      return (a.created?.seconds || 0) - (b.created?.seconds || 0);
    });

    return { filteredTasks: [...inbox, ...inView] };
  }, [tasks, filterStatus, searchQuery, currentViewRange, selectedCategory]);

  const TabButton = ({ isActive, onClick, label }) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "10px",
        border: "none",
        cursor: "pointer",
        backgroundColor: isActive ? "#333" : "#f0f0f0",
        color: isActive ? "#fff" : "#333",
        fontWeight: "bold",
      }}
    >
      {label}
    </button>
  );

  const handleQuickAdd = () => {
    const title = quickAddTitle.trim();
    if (!title) return;
    const cat = selectedCategory === "private" ? "private" : "work";
    addTask({
      title,
      category: cat,
      status: "未対応",
      estimatedHours: 0,
      isAllDay: false,
      start: null,
      end: null,
    });
    setQuickAddTitle("");
  };

  const handleQuickAddKeyDown = (e) => {
    if (e.key === "Enter" && !isComposing) {
      handleQuickAdd();
    }
  };

  const openNewTaskModal = () => {
    setEditingTask({});
    setIsModalOpen(true);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: isMobile ? "0" : "10px",
        overflow: "hidden",
      }}
    >
      {isMobile && (
        <div style={{ display: "flex", borderBottom: "1px solid #ddd" }}>
          <TabButton
            isActive={activeTab === "calendar"}
            onClick={() => setActiveTab("calendar")}
            label="カレンダー"
          />
          <TabButton
            isActive={activeTab === "tasks"}
            onClick={() => setActiveTab("tasks")}
            label="タスクリスト"
          />
        </div>
      )}

      <div
        style={{
          display: isMobile ? "block" : "flex",
          flex: 1,
          gap: isMobile ? "10px" : "0px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "100%",
            padding: isMobile ? "10px" : "0 6px 0 0",
            display: isMobile && activeTab !== "calendar" ? "none" : "block",
            position: "relative",
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            initialView={savedCalendarView}
            initialDate={savedCalendarDate}
            locale={jaLocale}
            firstDay={firstDayOfWeek !== undefined ? firstDayOfWeek : 6}
            slotMinTime={timeSlots?.min || "00:00:00"}
            slotMaxTime={timeSlots?.max || "24:00:00"}
            timeZone="local"
            scrollTime={new Date().toTimeString().slice(0, 8)}
            customButtons={{
              timeSlotBtn: {
                text: `${timeSlotLabel} ▾`,
                click: (e) => {
                  setTimeSlotAnchorEl(e.currentTarget);
                },
              },
            }}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "listDay,dayGridMonth,timeGridWeek,timeGridDay timeSlotBtn",
            }}
            buttonText={{
              today: "今日",
              listDay: "リスト",
              dayGridMonth: "月",
              timeGridWeek: "週",
              timeGridDay: "日",
            }}
            nowIndicator={true}
            allDaySlot={true}
            slotLabelFormat={{
              hour: "numeric",
              minute: "2-digit",
              hour12: false,
            }}
            height="100%"
            expandRows={true}
            views={{
              dayGridYear: {
                dayMaxEvents: 1,
              },
              dayGridMonth: {
                dayMaxEvents: 2,
              },
              timeGridWeek: {
                dayMaxEvents: 1,
              },
              timeGridDay: {
                dayMaxEvents: 1,
              },
            }}
            dayCellClassNames={(arg) => {
              const dateStr = toDateTimeStr(arg.date, true);
              if (hoveredTaskDate && hoveredTaskDate === dateStr) {
                return ["is-linked-day-hovered"];
              }
              return [];
            }}
            dayHeaderClassNames={(arg) => {
              const dateStr = toDateTimeStr(arg.date, true);
              if (hoveredTaskDate && hoveredTaskDate === dateStr) {
                return ["is-linked-day-hovered"];
              }
              return [];
            }}
            droppable={true}
            eventReceive={handleEventReceive}
            selectable={true}
            selectMirror={true}
            select={(selectInfo) => {
              const startStr = toDateTimeStr(
                selectInfo.start,
                selectInfo.allDay,
              );
              const endStr = selectInfo.allDay
                ? null
                : toDateTimeStr(selectInfo.end, selectInfo.allDay);
              setEditingTask({
                title: "",
                status: "未対応",
                start: startStr,
                end: endStr,
                isAllDay: selectInfo.allDay,
                estimatedHours: 0,
                memo: "",
              });
              setIsModalOpen(true);
              selectInfo.view.calendar.unselect();
            }}
            eventClick={(clickInfo) => {
              if (clickInfo.event.id.startsWith("holiday-")) return;
              const t = tasks.find((task) => task.id === clickInfo.event.id);
              if (t) {
                setEditingTask(t);
                setIsModalOpen(true);
              }
            }}
            events={[
              ...tasks
                .filter((t) => {
                  if (!t.start) return false;
                  if (selectedCategory && selectedCategory !== "all") {
                    const cat = t.category || "work";
                    if (cat !== selectedCategory) return false;
                  }
                  return true;
                })
                .map((t) => ({
                  id: t.id,
                  title: t.title,
                  start: t.start,
                  end: t.end,
                  allDay: t.isAllDay,
                  extendedProps: {
                    status: t.status,
                    estimatedHours: t.estimatedHours,
                    category: t.category || "work",
                  },
                })),
              ...holidays,
            ]}
            editable={true}
            droppable={true}
            eventResizableFromStart={true}
            eventDrop={handleEventChange}
            eventResize={handleEventChange}
            eventReceive={handleEventReceive}
            eventMouseEnter={(info) => {
              if (!info.event.id.startsWith("holiday-")) {
                setHoveredTaskId(info.event.id);
              }
            }}
            eventMouseLeave={() => {
              setHoveredTaskId(null);
            }}
            eventClassNames={(arg) => {
              const classes = [];
              if (arg.event.extendedProps?.status) {
                classes.push(`status-${statusToSlug(arg.event.extendedProps.status)}`);
              }
              if (arg.event.id === hoveredTaskId) {
                classes.push("is-linked-hovered");
              }
              return classes;
            }}
            datesSet={(dateInfo) => {
              setCurrentViewRange({
                start: dateInfo.start,
                end: dateInfo.end,
              });
              localStorage.setItem("fc_currentView", dateInfo.view.type);
              const activeDate = dateInfo.view.calendar.getDate();
              localStorage.setItem("fc_currentDate", activeDate.toISOString());
            }}
          />
        </div>

        {!isMobile && (
          <div
            className="separator"
            onMouseDown={startResizing}
          />
        )}

        <div
          style={{
            width: isMobile ? "100%" : taskWidth,
            height: "100%",
            display: isMobile && activeTab !== "tasks" ? "none" : "flex",
            flexDirection: "column",
            padding: isMobile ? "10px" : "0",
          }}
        >
            {/* --- サイドバーヘッダー（洗練されたタスク追加バー） --- */}
            <Box
              sx={{
                p: 1.2,
                pb: 1,
                display: "flex",
                gap: 0.8,
                alignItems: "center",
              }}
            >
              <TextField
                placeholder="新しいタスクを追加..."
                size="small"
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                onKeyDown={handleQuickAddKeyDown}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: "action.hover",
                    fontSize: "0.85rem",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: "background.paper",
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ color: "text.secondary", mr: 0.5 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: quickAddTitle.trim() ? (
                    <InputAdornment position="end">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleQuickAdd}
                        sx={{
                          height: 26,
                          minWidth: 44,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          borderRadius: "6px",
                          textTransform: "none",
                          boxShadow: "none",
                          px: 1,
                        }}
                      >
                        追加
                      </Button>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Tooltip title="詳細を入力して作成">
                <IconButton
                  onClick={openNewTaskModal}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    "&:hover": {
                      color: "primary.main",
                      borderColor: "primary.main",
                      bgcolor: "rgba(59, 130, 246, 0.06)",
                    },
                  }}
                >
                  <EditNoteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="絞り込み">
                <IconButton
                  onClick={() => setIsFilterOpen(true)}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor:
                      Object.values(filterStatus).some((v) => !v) || searchQuery
                        ? "primary.main"
                        : "divider",
                    bgcolor:
                      Object.values(filterStatus).some((v) => !v) || searchQuery
                        ? "rgba(59, 130, 246, 0.08)"
                        : "action.hover",
                    color:
                      Object.values(filterStatus).some((v) => !v) || searchQuery
                        ? "primary.main"
                        : "text.secondary",
                    "&:hover": {
                      color: "primary.main",
                      borderColor: "primary.main",
                      bgcolor: "rgba(59, 130, 246, 0.06)",
                    },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* --- 絞り込みモーダル (Dialog) --- */}
            <Dialog
              open={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>タスクの絞り込み</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      }}
                    >
                      ステータス
                    </Box>
                    <FormGroup row>
                      {statusOptions.map((status) => (
                        <FormControlLabel
                          key={status}
                          control={
                            <Checkbox
                              checked={filterStatus[status]}
                              onChange={(e) =>
                                setFilterStatus((prev) => ({
                                  ...prev,
                                  [status]: e.target.checked,
                                }))
                              }
                              size="small"
                            />
                          }
                          label={status}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                  <TextField
                    placeholder="タイトル検索"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={filterTitle}
                    onCompositionStart={() => {
                      isComposingRef.current = true;
                    }}
                    onCompositionEnd={(e) => {
                      isComposingRef.current = false;
                      setSearchQuery(e.target.value);
                    }}
                    onChange={(e) => {
                      setFilterTitle(e.target.value);
                      if (!isComposingRef.current) {
                        setSearchQuery(e.target.value);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setFilterTitle("");
                    setSearchQuery("");
                    setFilterStatus((prev) => {
                      const next = { ...prev };
                      Object.keys(next).forEach((k) => (next[k] = true));
                      return next;
                    });
                  }}
                >
                  リセット
                </Button>
                <Button
                  onClick={() => setIsFilterOpen(false)}
                  variant="contained"
                >
                  完了
                </Button>
              </DialogActions>
            </Dialog>

            {/* --- ミニマルなスライディング下線カテゴリタブ（カテゴリが2つ以上ある場合のみ表示） --- */}
            {(() => {
              const isPro = String(userPlan).toLowerCase() === "pro";
              const catsToShow = availableCategories || [];

              // カテゴリが2つ以上存在しない場合はタブ自体を非表示
              if (catsToShow.length < 2) return null;

              const tabItems = [
                { id: "all", label: "All", isLocked: false },
                ...catsToShow.map((cat) => ({
                  id: cat,
                  label: cat,
                  isLocked: !isPro,
                })),
              ];

              return (
                <CategorySlideTabs
                  tabs={tabItems}
                  activeTab={selectedCategory}
                  onTabChange={(catId) => setSelectedCategory(catId)}
                  onOpenPricing={onOpenPricing}
                />
              );
            })()}

            {/* --- タスクリスト (Swipe) --- */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <TaskList
                tasks={filteredTasks}
                setEditingTask={setEditingTask}
                setIsModalOpen={setIsModalOpen}
                editTask={editTask}
                draggableElRef={draggableElRef}
                isMobile={isMobile}
                deleteTask={deleteTask}
                hoveredTaskId={hoveredTaskId}
                setHoveredTaskId={setHoveredTaskId}
              />
            </div>
          </div>
      </div>

      {/* --- 時間帯切替ドロップダウンメニュー --- */}
      <Menu
        anchorEl={timeSlotAnchorEl}
        open={Boolean(timeSlotAnchorEl)}
        onClose={() => setTimeSlotAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            mt: 0.5,
            minWidth: 220,
            boxShadow:
              "0 10px 25px -5px rgba(0,0,0,0.12), 0 4px 6px -2px rgba(0,0,0,0.05)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
          >
            カレンダー表示時間帯
          </Typography>
        </Box>
        <MenuItem
          selected={calendarTimeMode === "daily"}
          onClick={() => {
            setCalendarTimeMode("daily");
            setTimeSlotAnchorEl(null);
          }}
          sx={{
            fontSize: "0.85rem",
            py: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>
            デイリー ({dailyMinTime || "07:00"} - {dailyMaxTime || "23:00"})
          </span>
          {calendarTimeMode === "daily" && (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          )}
        </MenuItem>
        <MenuItem
          selected={calendarTimeMode === "business"}
          onClick={() => {
            setCalendarTimeMode("business");
            setTimeSlotAnchorEl(null);
          }}
          sx={{
            fontSize: "0.85rem",
            py: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>
            ビジネス ({businessMinTime || "09:00"} -{" "}
            {businessMaxTime || "19:00"})
          </span>
          {calendarTimeMode === "business" && (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          )}
        </MenuItem>
        <MenuItem
          selected={calendarTimeMode === "focus"}
          onClick={() => {
            setCalendarTimeMode("focus");
            setTimeSlotAnchorEl(null);
          }}
          sx={{
            fontSize: "0.85rem",
            py: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>現在時刻フォーカス (±{focusHours}h)</span>
          {calendarTimeMode === "focus" && (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          )}
        </MenuItem>
        <MenuItem
          selected={calendarTimeMode === "all"}
          onClick={() => {
            setCalendarTimeMode("all");
            setTimeSlotAnchorEl(null);
          }}
          sx={{
            fontSize: "0.85rem",
            py: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>24時間全体 (00:00 - 24:00)</span>
          {calendarTimeMode === "all" && (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          )}
        </MenuItem>
        {calendarTimeMode === "custom" && (
          <MenuItem
            selected={true}
            onClick={() => setTimeSlotAnchorEl(null)}
            sx={{
              fontSize: "0.85rem",
              py: 1,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              カスタム ({customMinTime} - {customMaxTime})
            </span>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 16, color: "primary.main" }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Undo Snackbar */}
      <Snackbar
        open={undoState.open}
        autoHideDuration={6000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setUndoState((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={`「${undoState.taskTitle}」の時間を変更しました`}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              color="primary"
              size="small"
              variant="contained"
              onClick={handleUndo}
              startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                borderRadius: "14px",
                px: 1.5,
                py: 0.3,
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              元に戻す (Undo)
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setUndoState((prev) => ({ ...prev, open: false }))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{
          "& .MuiSnackbarContent-root": {
            bgcolor: "#1e293b",
            color: "#f8fafc",
            borderRadius: 3,
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
            fontSize: "0.85rem",
            fontWeight: 500,
          },
        }}
      />
    </div>
  );
};

// --- Mini 3-Month Calendar Widget for All Tasks ---
const MiniSingleMonth = ({
  targetDate,
  selectedDate,
  setSelectedDate,
  rangeMode,
  setRangeMode,
  dateRange,
  effectiveFirstDay,
}) => {
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth(); // 0-indexed

  const weekdaysAll = ["日", "月", "火", "水", "木", "金", "土"];
  const weekdays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const dayIdx = (effectiveFirstDay + i) % 7;
      return {
        label: weekdaysAll[dayIdx],
        dayIdx,
      };
    });
  }, [effectiveFirstDay]);

  const days = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const dayOfWeek = firstDayOfMonth.getDay();
    const startDiff = (dayOfWeek - effectiveFirstDay + 7) % 7;
    const startDate = new Date(year, month, 1 - startDiff);

    const list = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      list.push(d);
    }
    return list;
  }, [year, month, effectiveFirstDay]);

  const todayStr = useMemo(() => toDateTimeStr(new Date(), true), []);
  const selIso = useMemo(
    () => toDateTimeStr(new Date(selectedDate), true),
    [selectedDate],
  );

  return (
    <Box
      sx={{
        width: 170,
        p: "6px 8px",
        borderRadius: "10px",
        bgcolor: "var(--card-bg-default, #ffffff)",
        border: "1px solid var(--card-border-default, #e2e8f0)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        flexShrink: 0,
      }}
    >
      {/* 月タイトル */}
      <Typography
        sx={{
          fontSize: "0.74rem",
          fontWeight: 700,
          color: "text.primary",
          textAlign: "center",
          mb: 0.4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {year}年 {month + 1}月
      </Typography>

      {/* 曜日ヘッダー */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          textAlign: "center",
          mb: 0.2,
        }}
      >
        {weekdays.map((w, idx) => (
          <Typography
            key={idx}
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color:
                w.dayIdx === 0
                  ? "#ef4444"
                  : w.dayIdx === 6
                  ? "#2563eb"
                  : "text.secondary",
            }}
          >
            {w.label}
          </Typography>
        ))}
      </Box>

      {/* 日付グリッド */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          rowGap: "2px",
        }}
      >
        {days.map((d, i) => {
          const iso = toDateTimeStr(d, true);
          const isCurrentMonth = d.getMonth() === month;
          const isToday = iso === todayStr;
          const dayIdx = d.getDay();

          let isInRange = false;
          let isRangeStart = false;
          let isRangeEnd = false;
          let isSingleSelect = false;

          if (rangeMode === "day") {
            if (iso === selIso) {
              isSingleSelect = true;
            }
          } else if (dateRange) {
            if (iso >= dateRange.start && iso <= dateRange.end) {
              isInRange = true;
              if (iso === dateRange.start) isRangeStart = true;
              if (iso === dateRange.end) isRangeEnd = true;
            }
          }

          const textColor = (() => {
            if (isSingleSelect) return "#ffffff";
            if (!isCurrentMonth) return "rgba(0,0,0,0.18)";
            if (dayIdx === 0) return "#ef4444";
            if (dayIdx === 6) return "#2563eb";
            return "text.primary";
          })();

          return (
            <Box
              key={i}
              onClick={() => {
                setSelectedDate(d);
                if (rangeMode === "all") setRangeMode("day");
              }}
              sx={{
                position: "relative",
                height: 19,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                bgcolor:
                  isInRange && rangeMode !== "day"
                    ? "rgba(37, 99, 235, 0.12)"
                    : "transparent",
                borderRadius:
                  isInRange && rangeMode !== "day"
                    ? isRangeStart
                      ? "10px 0 0 10px"
                      : isRangeEnd
                      ? "0 10px 10px 0"
                      : "0"
                    : "0",
                "&:hover": {
                  "& .day-circle": {
                    bgcolor: isSingleSelect
                      ? "primary.dark"
                      : "rgba(37, 99, 235, 0.1)",
                  },
                },
              }}
            >
              <Box
                className="day-circle"
                sx={{
                  width: 17,
                  height: 17,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.66rem",
                  fontWeight: isToday || isSingleSelect ? 700 : 500,
                  fontVariantNumeric: "tabular-nums",
                  bgcolor: isSingleSelect ? "primary.main" : "transparent",
                  color: textColor,
                  transition: "all 0.1s ease",
                  border:
                    isToday && !isSingleSelect
                      ? "1px solid #2563eb"
                      : "none",
                }}
              >
                {d.getDate()}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const MiniThreeMonthCalendar = ({
  selectedDate,
  setSelectedDate,
  rangeMode,
  setRangeMode,
  dateRange,
  effectiveFirstDay,
}) => {
  const today = useMemo(() => new Date(), []);

  const prevMonth = useMemo(() => {
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
  }, [today]);

  const currMonth = useMemo(() => {
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, [today]);

  const nextMonth = useMemo(() => {
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }, [today]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        overflowX: "auto",
        maxWidth: "100%",
        pb: 0.5,
      }}
    >
      <MiniSingleMonth
        targetDate={prevMonth}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        dateRange={dateRange}
        effectiveFirstDay={effectiveFirstDay}
      />
      <MiniSingleMonth
        targetDate={currMonth}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        dateRange={dateRange}
        effectiveFirstDay={effectiveFirstDay}
      />
      <MiniSingleMonth
        targetDate={nextMonth}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        dateRange={dateRange}
        effectiveFirstDay={effectiveFirstDay}
      />
    </Box>
  );
};

// --- Page 2: All Tasks (Backlog & Management) ---
const AllTasksPage = ({
  tasks,
  setEditingTask,
  setIsModalOpen,
  editTask,
  firstDayOfWeek,
}) => {
  const [sortField, setSortField] = useState("start");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isComposing, setIsComposing] = useState(false);

  const effectiveFirstDay = Number(
    firstDayOfWeek !== undefined
      ? firstDayOfWeek
      : localStorage.getItem("firstDayOfWeek") || 6,
  );

  // 期間フィルター状態（localStorage 保存＆復元、初期値は「今日」）
  const [rangeMode, setRangeMode] = useState(() => {
    return localStorage.getItem("todaay_alltasks_range_mode") || "day";
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const saved = localStorage.getItem("todaay_alltasks_selected_date");
    if (saved) {
      const parsed = new Date(saved);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });

  useEffect(() => {
    if (rangeMode) {
      localStorage.setItem("todaay_alltasks_range_mode", rangeMode);
    }
  }, [rangeMode]);

  useEffect(() => {
    if (selectedDate) {
      localStorage.setItem(
        "todaay_alltasks_selected_date",
        selectedDate.toISOString(),
      );
    }
  }, [selectedDate]);

  // ページネーション状態（localStorage 永続化）
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("todaay_alltasks_page_size");
    if (saved !== null) {
      const parsed = Number(saved);
      return isNaN(parsed) ? 10 : parsed;
    }
    return 10;
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    localStorage.setItem("todaay_alltasks_page_size", String(pageSize));
  }, [pageSize]);

  // インラインステータス変更メニュー状態
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [statusMenuTargetTask, setStatusMenuTargetTask] = useState(null);

  // 3ヶ月ミニカレンダーの開閉状態（localStorage 永続化）
  const [calendarOpen, setCalendarOpen] = useState(() => {
    const saved = localStorage.getItem("todaay_alltasks_calendar_open");
    return saved !== null ? saved === "true" : true; // デフォルト: 開いた状態
  });

  useEffect(() => {
    localStorage.setItem("todaay_alltasks_calendar_open", String(calendarOpen));
  }, [calendarOpen]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // 前後移動・今日ボタン
  const handlePrev = () => {
    setSelectedDate((prev) => {
      const dt = new Date(prev);
      if (rangeMode === "day") {
        dt.setDate(dt.getDate() - 1);
      } else if (rangeMode === "week") {
        dt.setDate(dt.getDate() - 7);
      } else if (rangeMode === "month") {
        dt.setMonth(dt.getMonth() - 1);
      }
      return dt;
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      const dt = new Date(prev);
      if (rangeMode === "day") {
        dt.setDate(dt.getDate() + 1);
      } else if (rangeMode === "week") {
        dt.setDate(dt.getDate() + 7);
      } else if (rangeMode === "month") {
        dt.setMonth(dt.getMonth() + 1);
      }
      return dt;
    });
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // 期間計算（設定で指定された曜日から1週間）
  const dateRange = useMemo(() => {
    if (rangeMode === "all") return null;
    const dt = new Date(selectedDate);

    if (rangeMode === "day") {
      const iso = toDateTimeStr(dt, true);
      return { start: iso, end: iso };
    }
    if (rangeMode === "week") {
      const currentDay = dt.getDay(); // 0:Sun ... 6:Sat
      const diff = (currentDay - effectiveFirstDay + 7) % 7;
      const weekStart = new Date(dt);
      weekStart.setDate(dt.getDate() - diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return {
        start: toDateTimeStr(weekStart, true),
        end: toDateTimeStr(weekEnd, true),
      };
    }
    if (rangeMode === "month") {
      const y = dt.getFullYear();
      const m = dt.getMonth();
      const start = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const end = `${y}-${String(m + 1).padStart(2, "0")}-${String(
        lastDay,
      ).padStart(2, "0")}`;
      return { start, end };
    }
    return null;
  }, [rangeMode, selectedDate, effectiveFirstDay]);

  // FullCalendar風の期間タイトル
  const dateTitle = useMemo(() => {
    if (rangeMode === "all") return "すべての期間";
    const dt = new Date(selectedDate);
    const y = dt.getFullYear();
    const m = dt.getMonth() + 1;
    const d = dt.getDate();
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const w = weekdays[dt.getDay()];

    if (rangeMode === "day") {
      return `${y}年${m}月${d}日 (${w})`;
    }
    if (rangeMode === "week") {
      const currentDay = dt.getDay();
      const diff = (currentDay - effectiveFirstDay + 7) % 7;
      const weekStart = new Date(dt);
      weekStart.setDate(dt.getDate() - diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startM = weekStart.getMonth() + 1;
      const startD = weekStart.getDate();
      const startW = weekdays[weekStart.getDay()];
      const endM = weekEnd.getMonth() + 1;
      const endD = weekEnd.getDate();
      const endW = weekdays[weekEnd.getDay()];

      if (weekStart.getFullYear() === weekEnd.getFullYear()) {
        return `${weekStart.getFullYear()}年 ${startM}/${startD}(${startW}) 〜 ${endM}/${endD}(${endW})`;
      }
      return `${weekStart.getFullYear()}年${startM}/${startD}(${startW}) 〜 ${weekEnd.getFullYear()}年${endM}/${endD}(${endW})`;
    }
    if (rangeMode === "month") {
      return `${y}年${m}月`;
    }
    return "";
  }, [rangeMode, selectedDate, effectiveFirstDay]);

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = tasks.filter((task) => {
      const s = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !s ||
        (task.title && task.title.toLowerCase().includes(s)) ||
        (task.memo && task.memo.toLowerCase().includes(s)) ||
        (task.status && task.status.toLowerCase().includes(s)) ||
        (task.start && task.start.includes(s)) ||
        (task.end && task.end.includes(s)) ||
        (task.estimatedHours && String(task.estimatedHours).includes(s));

      if (!matchesSearch) return false;

      if (dateRange) {
        if (!task.start) return false;
        const taskDate = task.start.slice(0, 10);
        if (dateRange.start && taskDate < dateRange.start) return false;
        if (dateRange.end && taskDate > dateRange.end) return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "title":
          aVal = a.title || "";
          bVal = b.title || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "start":
          aVal = a.start || "";
          bVal = b.start || "";
          break;
        case "estimatedHours":
          aVal = Number(a.estimatedHours) || 0;
          bVal = Number(b.estimatedHours) || 0;
          break;
        case "created":
          aVal = a.created?.seconds || 0;
          bVal = b.created?.seconds || 0;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [tasks, searchQuery, dateRange, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, rangeMode, selectedDate]);

  const totalItems = filteredAndSortedTasks.length;
  const totalPages =
    pageSize === -1 ? 1 : Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedTasks = useMemo(() => {
    if (pageSize === -1) return filteredAndSortedTasks;
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredAndSortedTasks.slice(start, start + pageSize);
  }, [filteredAndSortedTasks, safeCurrentPage, pageSize]);

  const startItemIdx =
    totalItems === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const endItemIdx =
    pageSize === -1
      ? totalItems
      : Math.min(safeCurrentPage * pageSize, totalItems);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3.5 },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      {/* ページヘッダー ＆ 3ヶ月ミニカレンダー（アコーディオン開閉） */}
      <Box>
        {/* タイトル行 ＋ 開閉トグルボタン */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                letterSpacing: "-0.5px",
                fontSize: "1.35rem",
              }}
            >
              すべてのタスク
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: "0.84rem" }}
            >
              登録されているすべてのタスクを管理・検索します。
            </Typography>
          </Box>

          {/* カレンダー開閉トグルボタン */}
          <Box
            onClick={() => setCalendarOpen((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              px: 1.2,
              py: 0.6,
              borderRadius: "8px",
              bgcolor: calendarOpen
                ? "rgba(37, 99, 235, 0.06)"
                : "var(--card-bg-default, #ffffff)",
              border: "1px solid",
              borderColor: calendarOpen
                ? "rgba(37, 99, 235, 0.25)"
                : "var(--card-border-default, #e2e8f0)",
              color: calendarOpen ? "primary.main" : "text.secondary",
              userSelect: "none",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: calendarOpen
                  ? "rgba(37, 99, 235, 0.10)"
                  : "rgba(0,0,0,0.03)",
              },
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
              カレンダー
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: calendarOpen ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.25s ease",
              }}
            />
          </Box>
        </Box>

        {/* 3ヶ月ミニカレンダー（スライドアニメーション） */}
        <Box
          sx={{
            overflow: "hidden",
            maxHeight: calendarOpen ? 200 : 0,
            opacity: calendarOpen ? 1 : 0,
            transition: "max-height 0.3s ease, opacity 0.25s ease",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <MiniThreeMonthCalendar
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            rangeMode={rangeMode}
            setRangeMode={setRangeMode}
            dateRange={dateRange}
            effectiveFirstDay={effectiveFirstDay}
          />
        </Box>
      </Box>

      {/* コントロールバー（左: 検索バー / 右: 日付 < 今日 > 月 週 日 全期間） */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          placeholder="検索...（タイトル、メモ、ステータス、日時、工数）"
          size="small"
          value={searchText}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            setSearchQuery(e.target.value);
          }}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!isComposing) setSearchQuery(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 19, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "var(--card-bg-default, #ffffff)",
              fontSize: "0.85rem",
            },
          }}
        />

        {/* 期間ナビゲーション ＆ モード切替: [ 日付 ] [ < ] [ 今日 ] [ > ]  [ 月 | 週 | 日 | 全期間 ] */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* 日付 < 今日 > */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              bgcolor: "var(--card-bg-default, #ffffff)",
              border: "1px solid var(--card-border-default, #e2e8f0)",
              borderRadius: "10px",
              p: "3px 6px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
          >
            {/* 日付ラベル */}
            <Typography
              sx={{
                fontSize: "0.86rem",
                fontWeight: 700,
                px: 1,
                color: rangeMode === "all" ? "text.secondary" : "text.primary",
                minWidth: { xs: "auto", sm: 160 },
                textAlign: "center",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {dateTitle}
            </Typography>

            {/* < 前へ */}
            <IconButton
              size="small"
              onClick={handlePrev}
              disabled={rangeMode === "all"}
              sx={{ p: 0.4, color: "text.secondary" }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            {/* 今日 */}
            <Button
              size="small"
              onClick={handleToday}
              disabled={rangeMode === "all"}
              sx={{
                textTransform: "none",
                fontSize: "0.78rem",
                fontWeight: 600,
                px: 1.2,
                py: 0.3,
                minWidth: "auto",
                borderRadius: "6px",
                color: "text.primary",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              今日
            </Button>

            {/* > 次へ */}
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={rangeMode === "all"}
              sx={{ p: 0.4, color: "text.secondary" }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* モード切替 [ 月 | 週 | 日 | 全期間 ] */}
          <ToggleButtonGroup
            value={rangeMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode) setRangeMode(newMode);
            }}
            size="small"
            sx={{
              bgcolor: "var(--card-bg-default, #ffffff)",
              border: "1px solid var(--card-border-default, #e2e8f0)",
              borderRadius: "10px",
              p: "2px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
              "& .MuiToggleButton-root": {
                px: 1.4,
                py: 0.4,
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                border: "none",
                borderRadius: "8px !important",
                color: "text.secondary",
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "primary.dark" },
                },
              },
            }}
          >
            <ToggleButton value="month">月</ToggleButton>
            <ToggleButton value="week">週</ToggleButton>
            <ToggleButton value="day">日</ToggleButton>
            <ToggleButton value="all">全期間</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* インラインステータス変更メニュー */}
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          setStatusMenuAnchorEl(null);
          setStatusMenuTargetTask(null);
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            bgcolor: "var(--card-bg-default, #ffffff)",
            border: "1px solid var(--card-border-default, #e2e8f0)",
            p: 0.5,
            minWidth: "120px",
          },
        }}
      >
        {[
          "未対応",
          "対応中",
          "一旦",
          "完了",
          "保留",
          "連絡待ち",
          "対応不要",
        ].map((statusOption) => {
          const optConfig =
            STATUS_CONFIG[statusOption] || STATUS_CONFIG["未対応"];
          const isSelected =
            statusMenuTargetTask?.status === statusOption;
          return (
            <MenuItem
              key={statusOption}
              onClick={(e) => {
                if (statusMenuTargetTask && editTask) {
                  editTask(statusMenuTargetTask.id, { status: statusOption });
                }
                setStatusMenuAnchorEl(null);
                setStatusMenuTargetTask(null);
              }}
              sx={{
                borderRadius: "6px",
                fontSize: "0.75rem",
                fontWeight: isSelected ? 700 : 500,
                py: 0.6,
                px: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: isSelected ? optConfig.bg : "transparent",
                color: optConfig.text,
                "&:hover": {
                  bgcolor: optConfig.bg,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: optConfig.dot,
                  flexShrink: 0,
                }}
              />
              {statusOption}
            </MenuItem>
          );
        })}
      </Menu>

      {/* MUI Table 構造 */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid var(--card-border-default, #e2e8f0)",
          bgcolor: "var(--card-bg-default, #ffffff)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
        }}
      >
        <Table sx={{ minWidth: 800 }} size="medium">
          <TableHead
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.02)",
              "& .MuiTableCell-root": {
                whiteSpace: "nowrap",
                fontWeight: 700,
                fontSize: "0.82rem",
              },
            }}
          >
            <TableRow>
              {/* 1. 日付 */}
              <TableCell sx={{ width: 110 }}>
                <TableSortLabel
                  active={sortField === "start"}
                  direction={sortField === "start" ? sortDirection : "asc"}
                  onClick={() => handleSort("start")}
                >
                  日付
                </TableSortLabel>
              </TableCell>

              {/* 2. 曜日 */}
              <TableCell align="center" sx={{ width: 50 }}>
                曜日
              </TableCell>

              {/* 3. 開始時間 */}
              <TableCell sx={{ width: 85 }}>
                開始時間
              </TableCell>

              {/* 4. 工数 */}
              <TableCell sx={{ width: 75 }}>
                <TableSortLabel
                  active={sortField === "estimatedHours"}
                  direction={
                    sortField === "estimatedHours" ? sortDirection : "asc"
                  }
                  onClick={() => handleSort("estimatedHours")}
                >
                  工数
                </TableSortLabel>
              </TableCell>

              {/* 5. 終日 */}
              <TableCell align="center" sx={{ width: 55 }}>
                終日
              </TableCell>

              {/* 6. ステータス */}
              <TableCell sx={{ width: 125 }}>
                <TableSortLabel
                  active={sortField === "status"}
                  direction={sortField === "status" ? sortDirection : "asc"}
                  onClick={() => handleSort("status")}
                >
                  ステータス
                </TableSortLabel>
              </TableCell>

              {/* 7. タスク名 */}
              <TableCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortField === "title"}
                  direction={sortField === "title" ? sortDirection : "asc"}
                  onClick={() => handleSort("title")}
                >
                  タスク名
                </TableSortLabel>
              </TableCell>

              {/* 8. メモ */}
              <TableCell sx={{ minWidth: 220 }}>
                メモ
              </TableCell>

              {/* 9. 作成日 */}
              <TableCell sx={{ width: 105 }}>
                <TableSortLabel
                  active={sortField === "created"}
                  direction={sortField === "created" ? sortDirection : "asc"}
                  onClick={() => handleSort("created")}
                >
                  作成日
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 6, color: "text.secondary", fontSize: "0.9rem" }}
                >
                  条件に一致するタスクが見つかりません
                </TableCell>
              </TableRow>
            ) : (
              paginatedTasks.map((t) => {
                const statusStyle =
                  STATUS_CONFIG[t.status] || STATUS_CONFIG["未対応"];

                const dateStr = t.start
                  ? t.start.slice(0, 10).replace(/-/g, "/")
                  : "-";
                const startTimeStr =
                  !t.isAllDay && t.start && t.start.includes("T")
                    ? parseDateTime(t.start).time
                    : "-";

                // 曜日計算
                let weekdayLabel = "-";
                let weekdayColor = "text.secondary";
                if (t.start) {
                  const dayIdx = new Date(
                    t.start.slice(0, 10).replace(/-/g, "/"),
                  ).getDay();
                  weekdayLabel = ["日", "月", "火", "水", "木", "金", "土"][
                    dayIdx
                  ];
                  if (dayIdx === 0) weekdayColor = "#ef4444"; // 日曜：赤
                  if (dayIdx === 6) weekdayColor = "#2563eb"; // 土曜：青
                }

                return (
                  <TableRow
                    key={t.id}
                    hover
                    onClick={() => {
                      setEditingTask(t);
                      setIsModalOpen(true);
                    }}
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    {/* 1. 日付 */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "0.82rem",
                        color: "text.primary",
                      }}
                    >
                      {dateStr}
                    </TableCell>

                    {/* 2. 曜日 */}
                    <TableCell
                      align="center"
                      sx={{
                        py: 1.5,
                        whiteSpace: "nowrap",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: weekdayColor,
                      }}
                    >
                      {weekdayLabel}
                    </TableCell>

                    {/* 3. 開始時間 */}
                    <TableCell
                      sx={{
                        py: 1.5,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "0.82rem",
                        color:
                          startTimeStr !== "-"
                            ? "text.primary"
                            : "text.disabled",
                      }}
                    >
                      {startTimeStr}
                    </TableCell>

                    {/* 4. 工数 */}
                    <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.estimatedHours ? `${t.estimatedHours}h` : "-"}
                      </Typography>
                    </TableCell>

                    {/* 5. 終日 */}
                    <TableCell
                      align="center"
                      sx={{ py: 1.5, whiteSpace: "nowrap" }}
                    >
                      {t.isAllDay ? (
                        <CheckCircleOutlineIcon
                          sx={{
                            fontSize: 18,
                            color: "primary.main",
                            verticalAlign: "middle",
                          }}
                        />
                      ) : (
                        <span style={{ color: "#cbd5e1" }}>-</span>
                      )}
                    </TableCell>

                    {/* 6. ステータスバッジ */}
                    <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusMenuAnchorEl(e.currentTarget);
                          setStatusMenuTargetTask(t);
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          px: "7px",
                          py: "2px",
                          borderRadius: "12px",
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          lineHeight: 1.2,
                          bgcolor: statusStyle.badgeBg,
                          color: statusStyle.text,
                          border: `1px solid ${statusStyle.border}`,
                          cursor: "pointer",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            filter: "brightness(0.95)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: statusStyle.dot,
                            flexShrink: 0,
                            display: "inline-block",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.status}
                        </span>
                        <ArrowDropDownIcon
                          sx={{ fontSize: 14, ml: -0.4, opacity: 0.7 }}
                        />
                      </Box>
                    </TableCell>

                    {/* 7. タスク名 */}
                    <TableCell sx={{ py: 1.5, maxWidth: 260 }}>
                      <Typography
                        title={t.title}
                        sx={{
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {t.title}
                      </Typography>
                    </TableCell>

                    {/* 5. メモ / 詳細（1行省略表示・ホバーで全文確認可） */}
                    <TableCell sx={{ py: 1.5, maxWidth: 260 }}>
                      {t.memo ? (
                        <Typography
                          title={t.memo}
                          sx={{
                            fontSize: "0.82rem",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {t.memo}
                        </Typography>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </TableCell>

                    {/* 6. 作成日（1行表示） */}
                    <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.created?.seconds
                          ? new Date(
                              t.created.seconds * 1000,
                            ).toLocaleDateString("ja-JP")
                          : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* フッター: 表示件数切替 ＆ MUI Pagination */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          py: 1,
        }}
      >
        {/* 左側: 表示件数セレクターと範囲テキスト */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.82rem" }}
          >
            表示件数:
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            sx={{
              height: 32,
              fontSize: "0.82rem",
              borderRadius: "8px",
              minWidth: 70,
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={-1}>すべて</MenuItem>
          </Select>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.82rem", ml: 1 }}
          >
            {totalItems} 件中 {startItemIdx}-{endItemIdx} 件を表示
          </Typography>
        </Box>

        {/* 右側: MUI公式 Pagination コンポーネント */}
        {pageSize !== -1 && totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={safeCurrentPage}
            onChange={(e, p) => setCurrentPage(p)}
            color="primary"
            shape="rounded"
            size="medium"
            showFirstButton
            showLastButton
          />
        )}
      </Box>
    </Box>
  );
};

// --- Page 3: Settings ---
const SettingsPage = ({
  calendarTimeMode,
  setCalendarTimeMode,
  focusHours,
  setFocusHours,
  businessMinTime,
  setBusinessMinTime,
  businessMaxTime,
  setBusinessMaxTime,
  dailyMinTime,
  dailyMaxTime,
  customMinTime,
  setCustomMinTime,
  customMaxTime,
  setCustomMaxTime,
  firstDayOfWeek,
  setFirstDayOfWeek,
  hourlyChimeEnabled,
  setHourlyChimeEnabled,
  hourlyChimeInterval,
  setHourlyChimeInterval,
  triggerTestChime,
  themeMode,
  setThemeMode,
  registeredCategories = ["仕事", "プライベート"],
  setRegisteredCategories,
  tasks = [],
  editTask,
  deleteTask,
}) => {
  const [newCatInput, setNewCatInput] = useState("");
  const [editingCatName, setEditingCatName] = useState(null);
  const [deleteConfirmInfo, setDeleteConfirmInfo] = useState(null); // { categoryName, count, taskIds }

  const handleAddCategory = () => {
    const trimmed = newCatInput.trim();
    if (!trimmed) return;
    if (registeredCategories.includes(trimmed)) return;
    if (setRegisteredCategories) {
      setRegisteredCategories([...registeredCategories, trimmed]);
    }
    setNewCatInput("");
  };

  const handleRemoveCategory = (catToRemove) => {
    // 該当カテゴリを持つタスクを抽出
    const matchingTasks = (tasks || []).filter((t) => t.category === catToRemove);

    if (matchingTasks.length === 0) {
      // 紐づくタスクがない場合は即座に削除
      if (setRegisteredCategories) {
        setRegisteredCategories(
          registeredCategories.filter((c) => c !== catToRemove)
        );
      }
    } else {
      // 1件以上ある場合は安全確認モーダルを表示
      setDeleteConfirmInfo({
        categoryName: catToRemove,
        count: matchingTasks.length,
        taskIds: matchingTasks.map((t) => t.id),
      });
    }
  };

  // 選択肢1: タスクを「未分類」にしてカテゴリのみ削除（安全・推奨）
  const handleUnassignAndRemoveCategory = () => {
    if (!deleteConfirmInfo) return;
    const { categoryName } = deleteConfirmInfo;

    // 該当タスクのカテゴリを未分類（""）に更新
    if (editTask && tasks) {
      tasks.forEach((t) => {
        if (t.category === categoryName) {
          editTask(t.id, { category: "" });
        }
      });
    }

    if (setRegisteredCategories) {
      setRegisteredCategories(
        registeredCategories.filter((c) => c !== categoryName)
      );
    }
    setDeleteConfirmInfo(null);
  };

  // 選択肢2: 関連するタスクもすべて一緒に削除（警告）
  const handleDeleteTasksAndCategory = () => {
    if (!deleteConfirmInfo) return;
    const { categoryName, taskIds } = deleteConfirmInfo;

    // 該当タスクを全削除
    if (deleteTask && taskIds) {
      taskIds.forEach((id) => {
        deleteTask(id);
      });
    }

    if (setRegisteredCategories) {
      setRegisteredCategories(
        registeredCategories.filter((c) => c !== categoryName)
      );
    }
    setDeleteConfirmInfo(null);
  };

  const handleRenameCategory = () => {
    if (!editingCatName || !editingCatName.newName.trim()) return;
    const { oldName, newName } = editingCatName;
    const trimmedNew = newName.trim();
    if (oldName === trimmedNew) {
      setEditingCatName(null);
      return;
    }
    if (setRegisteredCategories) {
      setRegisteredCategories(
        registeredCategories.map((c) => (c === oldName ? trimmedNew : c))
      );
    }
    if (editTask && tasks) {
      tasks.forEach((t) => {
        if (t.category === oldName) {
          editTask(t.id, { category: trimmedNew });
        }
      });
    }
    setEditingCatName(null);
  };

  return (
    <Box
      component="main"
      sx={{
        maxWidth: 680,
        mx: "auto",
        p: { xs: 2, sm: 3 },
        pb: 6,
      }}
    >
      <Typography
        component="h1"
        variant="h5"
        fontWeight="700"
        sx={{ mb: 3 }}
      >
        設定
      </Typography>

      <Stack spacing={3}>
        {/* テーマ・外観設定 */}
        <Card
          variant="outlined"
          sx={{ borderRadius: 2.5, boxShadow: "none" }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight="700"
              sx={{ mb: 0.5 }}
            >
              テーマ・外観
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              画面の配色モードを選択します。ヘッダーのボタンからも素早く切り替えられます。
            </Typography>

            <ToggleButtonGroup
              value={themeMode}
              exclusive
              onChange={(_, val) => {
                if (val !== null && setThemeMode) setThemeMode(val);
              }}
              size="small"
              fullWidth
              aria-label="テーマの選択"
              sx={{
                "& .MuiToggleButton-root": {
                  py: 0.9,
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  textTransform: "none",
                },
              }}
            >
              <ToggleButton value="light" aria-label="ライトテーマ">
                ライト
              </ToggleButton>
              <ToggleButton value="dark" aria-label="ダークテーマ">
                ダーク
              </ToggleButton>
              <ToggleButton value="system" aria-label="システム設定に従う">
                システム設定に合わせる
              </ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>

        {/* カテゴリ管理設定 */}
        <Card
          variant="outlined"
          sx={{ borderRadius: 2.5, boxShadow: "none" }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight="700"
              sx={{ mb: 0.5 }}
            >
              カテゴリの管理
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              タスクを分類するカテゴリを登録・編集します。名前を変更すると既存タスクにも自動で反映されます。
            </Typography>

            {/* 登録済みカテゴリのリスト */}
            <Stack spacing={1} sx={{ mb: 2 }}>
              {registeredCategories.map((cat) => (
                <Box
                  key={cat}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" fontWeight="600">
                    {cat}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setEditingCatName({ oldName: cat, newName: cat })
                      }
                      aria-label={`${cat} の名前を変更`}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCategory(cat)}
                      aria-label={`${cat} を削除`}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {registeredCategories.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  登録されているカテゴリはありません
                </Typography>
              )}
            </Stack>

            {/* 新規カテゴリ追加入力欄 */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                placeholder="新しいカテゴリ名"
                value={newCatInput}
                onChange={(e) => setNewCatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCategory();
                  }
                }}
                sx={{ flex: 1 }}
                inputProps={{ "aria-label": "新しいカテゴリ名" }}
              />
              <Button
                variant="contained"
                onClick={handleAddCategory}
                disabled={!newCatInput.trim()}
                sx={{ textTransform: "none", fontWeight: "bold", px: 2 }}
              >
                追加
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* カテゴリ名変更モーダル */}
        <Dialog
          open={Boolean(editingCatName)}
          onClose={() => setEditingCatName(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>カテゴリ名の変更</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="カテゴリ名"
              fullWidth
              size="small"
              variant="outlined"
              value={editingCatName ? editingCatName.newName : ""}
              onChange={(e) =>
                setEditingCatName((prev) =>
                  prev ? { ...prev, newName: e.target.value } : null
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleRenameCategory();
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditingCatName(null)}>キャンセル</Button>
            <Button
              onClick={handleRenameCategory}
              variant="contained"
              disabled={!editingCatName || !editingCatName.newName.trim()}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* カテゴリ削除時の安全確認モーダル */}
        <Dialog
          open={Boolean(deleteConfirmInfo)}
          onClose={() => setDeleteConfirmInfo(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            カテゴリの削除確認
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
              「<strong>{deleteConfirmInfo?.categoryName}</strong>」には{" "}
              <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                {deleteConfirmInfo?.count}件
              </Box>{" "}
              のタスクが登録されています。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: "0.82rem" }}>
              カテゴリ削除後のタスクの扱いを選択してください：
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleUnassignAndRemoveCategory}
              sx={{ fontWeight: 700, textTransform: "none", py: 1 }}
            >
              タスクを未分類にしてカテゴリのみ削除（推奨）
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={handleDeleteTasksAndCategory}
              sx={{ fontWeight: 600, textTransform: "none", py: 0.9 }}
            >
              {deleteConfirmInfo?.count}件のタスクもすべて削除する
            </Button>
            <Button
              fullWidth
              variant="text"
              color="inherit"
              onClick={() => setDeleteConfirmInfo(null)}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              キャンセル
            </Button>
          </DialogActions>
        </Dialog>

        {/* カレンダー表示時間設定 */}
        <Card
          variant="outlined"
          sx={{ borderRadius: 2.5, boxShadow: "none" }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight="700"
              sx={{ mb: 0.5 }}
            >
              カレンダーの表示時間（週・日表示）
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              タイムラインに表示する時間帯を選択します。不要な早朝・深夜を省いて画面をスッキリ使えます。
            </Typography>

            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <RadioGroup
                value={calendarTimeMode}
                onChange={(e) => setCalendarTimeMode(e.target.value)}
                aria-label="カレンダーの表示時間帯"
              >
                <Stack spacing={1.2}>
                  {/* ビジネス */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        calendarTimeMode === "business"
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        calendarTimeMode === "business"
                          ? "rgba(59, 130, 246, 0.04)"
                          : "transparent",
                    }}
                    onClick={() => setCalendarTimeMode("business")}
                  >
                    <FormControlLabel
                      value="business"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            ビジネス（{businessMinTime || "09:00"} 〜{" "}
                            {businessMaxTime || "19:00"}）
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            日中の仕事・作業時間にフォーカス（時刻変更可能）
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                    {calendarTimeMode === "business" && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pl: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TimeSelectDropdown
                          label="開始時刻"
                          value={businessMinTime}
                          onChange={setBusinessMinTime}
                          placeholder="09:00"
                        />
                        <Typography color="text.secondary">〜</Typography>
                        <TimeSelectDropdown
                          label="終了時刻"
                          value={businessMaxTime}
                          onChange={setBusinessMaxTime}
                          placeholder="19:00"
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* デイリー */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        calendarTimeMode === "daily"
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        calendarTimeMode === "daily"
                          ? "rgba(59, 130, 246, 0.04)"
                          : "transparent",
                    }}
                    onClick={() => setCalendarTimeMode("daily")}
                  >
                    <FormControlLabel
                      value="daily"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            デイリー（{dailyMinTime || "07:00"} 〜{" "}
                            {dailyMaxTime || "23:00"}）
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            朝活から夜のタスクまで生活全般をカバー（時刻変更可能）
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                    {calendarTimeMode === "daily" && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pl: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TimeSelectDropdown
                          label="開始時刻"
                          value={dailyMinTime}
                          onChange={setDailyMinTime}
                          placeholder="07:00"
                        />
                        <Typography color="text.secondary">〜</Typography>
                        <TimeSelectDropdown
                          label="終了時刻"
                          value={dailyMaxTime}
                          onChange={setDailyMaxTime}
                          placeholder="23:00"
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* 現在時刻フォーカス */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        calendarTimeMode === "focus"
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        calendarTimeMode === "focus"
                          ? "rgba(59, 130, 246, 0.04)"
                          : "transparent",
                    }}
                    onClick={() => setCalendarTimeMode("focus")}
                  >
                    <FormControlLabel
                      value="focus"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            現在時刻フォーカス（リアルタイム前後 ±n時間）
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            現在の時刻を中心に直近の時間帯だけを表示
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                    {calendarTimeMode === "focus" && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pl: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Typography variant="body2" color="text.secondary">
                          表示範囲: 現在時刻の
                        </Typography>
                        <Select
                          size="small"
                          value={focusHours}
                          onChange={(e) =>
                            setFocusHours(Number(e.target.value))
                          }
                          sx={{ minWidth: 130, height: 36 }}
                        >
                          <MenuItem value={2}>前後 ±2時間</MenuItem>
                          <MenuItem value={3}>前後 ±3時間</MenuItem>
                          <MenuItem value={4}>前後 ±4時間</MenuItem>
                          <MenuItem value={6}>前後 ±6時間</MenuItem>
                        </Select>
                      </Box>
                    )}
                  </Paper>

                  {/* カスタム */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        calendarTimeMode === "custom"
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        calendarTimeMode === "custom"
                          ? "rgba(59, 130, 246, 0.04)"
                          : "transparent",
                    }}
                    onClick={() => setCalendarTimeMode("custom")}
                  >
                    <FormControlLabel
                      value="custom"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            カスタム指定
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            お好みの開始時刻と終了時刻を指定
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                    {calendarTimeMode === "custom" && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pl: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TimeSelectDropdown
                          label="開始時刻"
                          value={customMinTime}
                          onChange={setCustomMinTime}
                          placeholder="08:00"
                        />
                        <Typography color="text.secondary">〜</Typography>
                        <TimeSelectDropdown
                          label="終了時刻"
                          value={customMaxTime}
                          onChange={setCustomMaxTime}
                          placeholder="22:00"
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* 24時間全体 */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        calendarTimeMode === "all" ? "primary.main" : "divider",
                      bgcolor:
                        calendarTimeMode === "all"
                          ? "rgba(59, 130, 246, 0.04)"
                          : "transparent",
                    }}
                    onClick={() => setCalendarTimeMode("all")}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            24時間全体（00:00 〜 24:00）
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            1日の全時間を表示
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                  </Paper>
                </Stack>
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        {/* 週の開始曜日設定 */}
        <Card
          variant="outlined"
          sx={{ borderRadius: 2.5, boxShadow: "none" }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight="700"
              sx={{ mb: 0.5 }}
            >
              週の開始曜日
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              カレンダーの1週間の始まりの曜日を設定します。
            </Typography>

            <Select
              size="small"
              value={firstDayOfWeek}
              onChange={(e) => setFirstDayOfWeek(Number(e.target.value))}
              sx={{ minWidth: 170, height: 38 }}
            >
              <MenuItem value={1}>月曜日始まり</MenuItem>
              <MenuItem value={2}>火曜日始まり</MenuItem>
              <MenuItem value={3}>水曜日始まり</MenuItem>
              <MenuItem value={4}>木曜日始まり</MenuItem>
              <MenuItem value={5}>金曜日始まり</MenuItem>
              <MenuItem value={6}>土曜日始まり</MenuItem>
              <MenuItem value={0}>日曜日始まり</MenuItem>
            </Select>
          </CardContent>
        </Card>

        {/* 定期時報通知設定 */}
        <Card
          variant="outlined"
          sx={{ borderRadius: 2.5, boxShadow: "none" }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                component="h2"
                variant="subtitle1"
                fontWeight="700"
              >
                定期時報通知
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={hourlyChimeEnabled}
                    onChange={(e) => setHourlyChimeEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={hourlyChimeEnabled ? "ON" : "OFF"}
                sx={{ m: 0 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              指定した間隔ごとに画面右上に「現在 XX:XX です」と1.5秒間さりげなく通知します。
            </Typography>

            {hourlyChimeEnabled && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    通知頻度:
                  </Typography>
                  <Select
                    size="small"
                    value={hourlyChimeInterval}
                    onChange={(e) => setHourlyChimeInterval(Number(e.target.value))}
                    sx={{ minWidth: 170, height: 38 }}
                    aria-label="通知頻度"
                  >
                    <MenuItem value={15}>15分ごと（毎正時・15分・30分・45分）</MenuItem>
                    <MenuItem value={30}>30分ごと（毎正時・30分）</MenuItem>
                    <MenuItem value={60}>1時間ごと（毎正時 :00）</MenuItem>
                    <MenuItem value={120}>2時間ごと</MenuItem>
                  </Select>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={triggerTestChime}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  通知をテスト
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

// --- Login Screen ---
const LoginScreen = ({ onLogin }) => {
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        bgcolor: "#f5f5f5",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <DashboardIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
        <Typography variant="h4" fontWeight="bold">
          ToDaay
        </Typography>
        <Typography variant="body1" color="text.secondary">
          今この瞬間に集中する
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="large"
        startIcon={<LoginIcon />}
        onClick={onLogin}
        sx={{
          py: 1.5,
          px: 4,
          fontWeight: "bold",
          fontSize: "1.1rem",
        }}
      >
        Googleでログイン
      </Button>
    </Box>
  );
};

// --- Task Prompt Toast (さりげないミニマルトースト通知) ---
const TaskPromptToast = ({ tasks, editTask }) => {
  const [startPromptTask, setStartPromptTask] = useState(null);
  const [endPromptTask, setEndPromptTask] = useState(null);

  const notifiedKeysRef = useRef(new Set());
  const snoozedUntilRef = useRef(new Map());

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const checkTasks = () => {
      const now = new Date();
      const nowTime = now.getTime();

      tasks.forEach((task) => {
        if (!task.id || !task.start || task.isAllDay) return;

        const startTimestamp = new Date(task.start).getTime();
        if (isNaN(startTimestamp)) return;

        // 1. 開始時間通知チェック
        const startKey = `start-${task.id}-${task.start}`;
        const snoozedUntil = snoozedUntilRef.current.get(task.id) || 0;

        if (
          task.status === "未対応" &&
          nowTime >= startTimestamp &&
          nowTime - startTimestamp <= 30 * 60 * 1000 &&
          nowTime > snoozedUntil &&
          !notifiedKeysRef.current.has(startKey) &&
          !startPromptTask &&
          !endPromptTask
        ) {
          notifiedKeysRef.current.add(startKey);
          setStartPromptTask(task);

          if (
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            try {
              new Notification("🔔 タスク開始の時間です", {
                body: `「${task.title}」を開始しますか？`,
              });
            } catch (e) {}
          }
          return;
        }

        // 2. 終了時間通知チェック
        let endTimestamp = null;
        if (task.end) {
          endTimestamp = new Date(task.end).getTime();
        } else if (task.estimatedHours && task.estimatedHours > 0) {
          endTimestamp = startTimestamp + task.estimatedHours * 60 * 60 * 1000;
        }

        if (!endTimestamp || isNaN(endTimestamp)) return;

        const endKey = `end-${task.id}-${task.end || endTimestamp}`;
        if (
          task.status !== "完了" &&
          task.status !== "対応不要" &&
          nowTime >= endTimestamp &&
          nowTime - endTimestamp <= 60 * 60 * 1000 &&
          !notifiedKeysRef.current.has(endKey) &&
          !startPromptTask &&
          !endPromptTask
        ) {
          notifiedKeysRef.current.add(endKey);
          setEndPromptTask({ ...task, computedEndTimestamp: endTimestamp });

          if (
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            try {
              new Notification("⏰ タスク終了の時間です", {
                body: `「${task.title}」が終了時刻になりました。延長しますか？`,
              });
            } catch (e) {}
          }
          return;
        }
      });
    };

    checkTasks();
    const interval = setInterval(checkTasks, 10000);
    return () => clearInterval(interval);
  }, [tasks, startPromptTask, endPromptTask]);

  const handleStartTask = () => {
    if (startPromptTask) {
      const now = new Date();
      const nowStr = toDateTimeStr(now, false);
      const updates = {
        status: "対応中",
        start: nowStr,
      };

      // 工数または元の期間に基づいて終了時間も現在時刻に合わせて移動
      let durationMs = 0;
      if (
        startPromptTask.estimatedHours &&
        startPromptTask.estimatedHours > 0
      ) {
        durationMs = startPromptTask.estimatedHours * 60 * 60 * 1000;
      } else if (startPromptTask.start && startPromptTask.end) {
        const origDiff =
          new Date(startPromptTask.end).getTime() -
          new Date(startPromptTask.start).getTime();
        if (origDiff > 0) durationMs = origDiff;
      }

      if (durationMs > 0) {
        const newEnd = new Date(now.getTime() + durationMs);
        updates.end = toDateTimeStr(newEnd, false);
      }

      editTask(startPromptTask.id, updates);
      setStartPromptTask(null);
    }
  };

  const handleSnoozeStart = () => {
    if (startPromptTask) {
      const now = new Date();
      // 現在時刻を基準に5分後を計算
      const newStart = new Date(now.getTime() + 5 * 60 * 1000);
      const newStartStr = toDateTimeStr(newStart, false);

      const updates = { start: newStartStr };

      // 工数または元の所要時間に基づいて終了時間もスライド
      let durationMs = 0;
      if (
        startPromptTask.estimatedHours &&
        startPromptTask.estimatedHours > 0
      ) {
        durationMs = startPromptTask.estimatedHours * 60 * 60 * 1000;
      } else if (startPromptTask.start && startPromptTask.end) {
        const origDiff =
          new Date(startPromptTask.end).getTime() -
          new Date(startPromptTask.start).getTime();
        if (origDiff > 0) durationMs = origDiff;
      }

      if (durationMs > 0) {
        const newEnd = new Date(newStart.getTime() + durationMs);
        updates.end = toDateTimeStr(newEnd, false);
      }

      editTask(startPromptTask.id, updates);

      snoozedUntilRef.current.set(
        startPromptTask.id,
        newStart.getTime(),
      );
      const startKey = `start-${startPromptTask.id}-${startPromptTask.start}`;
      notifiedKeysRef.current.delete(startKey);
      setStartPromptTask(null);
    }
  };

  const handleCompleteTask = () => {
    if (endPromptTask) {
      editTask(endPromptTask.id, { status: "完了" });
      setEndPromptTask(null);
    }
  };

  const handleExtendTask = (minutes) => {
    if (endPromptTask) {
      const scheduledEndMs = endPromptTask.end
        ? new Date(endPromptTask.end).getTime()
        : endPromptTask.computedEndTimestamp;
      // 現在時刻と予定終了時刻の遅い方を基準に延長
      const baseMs = Math.max(scheduledEndMs, Date.now());
      const newEnd = new Date(baseMs + minutes * 60 * 1000);
      const newEndStr = toDateTimeStr(newEnd, false);

      let newHours = endPromptTask.estimatedHours || 0;
      if (endPromptTask.start) {
        const diffMs =
          newEnd.getTime() - new Date(endPromptTask.start).getTime();
        newHours =
          Math.max(0, Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10);
      }

      editTask(endPromptTask.id, {
        end: newEndStr,
        estimatedHours: newHours,
      });
      setEndPromptTask(null);
    }
  };

  if (!startPromptTask && !endPromptTask) return null;

  const startTimeStr = startPromptTask
    ? parseDateTime(startPromptTask.start).time
    : "";
  const endTimeStr = endPromptTask
    ? endPromptTask.end
      ? parseDateTime(endPromptTask.end).time
      : parseDateTime(
          new Date(endPromptTask.computedEndTimestamp).toISOString(),
        ).time
    : "";

  return (
    <Box
      className="subtle-task-toast"
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1400,
        width: { xs: "calc(100% - 32px)", sm: "360px" },
        maxWidth: "380px",
        borderRadius: "14px",
        bgcolor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(16px)",
        boxShadow:
          "0 12px 32px -4px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)",
        border: "1px solid rgba(226, 232, 240, 0.9)",
        p: 1.8,
        display: "flex",
        flexDirection: "column",
        gap: 1.2,
      }}
    >
      {/* 開始トースト */}
      {startPromptTask && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  placeContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  bgcolor: "#eff6ff",
                  color: "#2563eb",
                  flexShrink: 0,
                }}
              >
                <NotificationsActiveIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.83rem",
                  fontWeight: 700,
                  color: "var(--card-text-primary, #0f172a)",
                }}
              >
                {startTimeStr
                  ? `${startTimeStr}〜 のタスクを開始しますか？`
                  : "タスクを開始しますか？"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setStartPromptTask(null)}
              sx={{ p: 0.4, opacity: 0.5, "&:hover": { opacity: 1 } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontSize: "0.74rem",
              color: "var(--card-text-secondary, #64748b)",
              pl: 4.2,
              pr: 1,
              mt: -0.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            タスク名: {startPromptTask.title}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              pt: 0.2,
            }}
          >
            <Button
              onClick={handleSnoozeStart}
              size="small"
              variant="text"
              sx={{
                fontSize: "0.72rem",
                color: "var(--card-text-secondary, #64748b)",
                px: 1.2,
                py: 0.4,
                borderRadius: "20px",
                textTransform: "none",
                minWidth: "auto",
              }}
            >
              5分後
            </Button>
            <Button
              onClick={handleStartTask}
              size="small"
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon sx={{ fontSize: 15 }} />}
              sx={{
                fontSize: "0.74rem",
                fontWeight: "bold",
                px: 1.8,
                py: 0.4,
                borderRadius: "20px",
                textTransform: "none",
                minWidth: "auto",
                boxShadow: "none",
              }}
            >
              開始する
            </Button>
          </Box>
        </>
      )}

      {/* 終了延長トースト */}
      {endPromptTask && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  placeContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  bgcolor: "#fff7ed",
                  color: "#ea580c",
                  flexShrink: 0,
                }}
              >
                <AlarmIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.83rem",
                  fontWeight: 700,
                  color: "var(--card-text-primary, #0f172a)",
                }}
              >
                {endTimeStr
                  ? `${endTimeStr} 終了予定のタスクです`
                  : "タスク終了時刻になりました"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setEndPromptTask(null)}
              sx={{ p: 0.4, opacity: 0.5, "&:hover": { opacity: 1 } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontSize: "0.74rem",
              color: "var(--card-text-secondary, #64748b)",
              pl: 4.2,
              pr: 1,
              mt: -0.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            タスク名: {endPromptTask.title}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 0.8,
              pt: 0.2,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.6 }}>
              <Button
                onClick={() => handleExtendTask(15)}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.72rem",
                  px: 1.1,
                  py: 0.3,
                  borderRadius: "16px",
                  borderColor: "rgba(15, 23, 42, 0.14)",
                  color: "var(--card-text-primary, #0f172a)",
                  textTransform: "none",
                  minWidth: "auto",
                  "&:hover": {
                    borderColor: "#3b82f6",
                    bgcolor: "rgba(59, 130, 246, 0.04)",
                  },
                }}
              >
                +15分
              </Button>
              <Button
                onClick={() => handleExtendTask(30)}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.72rem",
                  px: 1.1,
                  py: 0.3,
                  borderRadius: "16px",
                  borderColor: "rgba(15, 23, 42, 0.14)",
                  color: "var(--card-text-primary, #0f172a)",
                  textTransform: "none",
                  minWidth: "auto",
                  "&:hover": {
                    borderColor: "#3b82f6",
                    bgcolor: "rgba(59, 130, 246, 0.04)",
                  },
                }}
              >
                +30分
              </Button>
            </Box>
            <Button
              onClick={handleCompleteTask}
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
              sx={{
                fontSize: "0.74rem",
                fontWeight: "bold",
                px: 1.6,
                py: 0.4,
                borderRadius: "20px",
                textTransform: "none",
                minWidth: "auto",
                boxShadow: "none",
              }}
            >
              完了にする
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// --- Theme Mode Toggle Component (1タップ切り替え) ---
const ThemeModeToggle = ({ themeMode, setThemeMode, effectiveDarkMode }) => {
  const handleToggle = () => {
    // 現在がダーク（明示的orシステム追従）ならライトへ、ライトならダークへ切り替え
    setThemeMode(effectiveDarkMode ? "light" : "dark");
  };

  return (
    <Tooltip title={effectiveDarkMode ? "ライトモードに切り替え" : "ダークモードに切り替え"}>
      <IconButton size="small" onClick={handleToggle} color="inherit" sx={{ p: 0.8 }}>
        {effectiveDarkMode ? (
          <LightModeOutlinedIcon fontSize="small" />
        ) : (
          <DarkModeOutlinedIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

// --- Pricing Modal (Stripe サブスクリプション案内) ---
const PricingModal = ({ open, onClose, user, onLogin, userPlan }) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleCheckout = async () => {
    if (!user) {
      if (onLogin) onLogin();
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const priceId = process.env.REACT_APP_STRIPE_PRICE_ID;
    const checkoutUrl =
      process.env.REACT_APP_CREATE_CHECKOUT_URL ||
      "https://us-central1-todaay-codesandbox.cloudfunctions.net/createCheckoutSession";

    try {
      const res = await fetch(checkoutUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          priceId: priceId,
        }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setErrorMsg(data.error || "決済セッションの作成に失敗しました");
      }
    } catch (e) {
      console.error(e);
      setErrorMsg("通信エラーが発生しました。もう一度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const isPro = userPlan === "pro";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pt: 3, pb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {isPro ? "✨ Pro プラン加入中" : "⚡ Todaaay Pro にアップグレード"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 1 }}>
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Typography variant="h4" fontWeight="800" color="primary.main">
            ¥380 <Typography component="span" variant="body2" color="text.secondary">/ 月</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            すべての制限を解除して快適に
          </Typography>
        </Box>

        <Stack
          spacing={1.5}
          sx={{
            mb: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="body2" color="text.primary">
              カテゴリ分け（仕事/プライベート等のタブ切り替え）
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="body2" color="text.primary">
              タスク作成数 <strong>無制限</strong>（無料は50件）
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="body2" color="text.primary">
              クラウド自動同期（複数端末で利用可）
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="body2" color="text.primary">
              設定・テーマのクラウド自動保存
            </Typography>
          </Stack>
        </Stack>

        {errorMsg && (
          <Typography color="error" variant="caption" display="block" sx={{ mb: 1, textAlign: "center" }}>
            {errorMsg}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", gap: 1 }}>
        {isPro ? (
          <Button fullWidth variant="outlined" color="success" disabled sx={{ py: 1.2 }}>
            ✅ 現在 Pro プランが適用されています
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleCheckout}
            disabled={loading}
            sx={{
              py: 1.3,
              fontWeight: "bold",
              background: "linear-gradient(45deg, #2563eb 30%, #3b82f6 90%)",
            }}
          >
            {loading
              ? "準備中..."
              : !user
              ? "Googleでログインしてアップグレード"
              : "¥380/月 で今すぐアップグレード"}
          </Button>
        )}
        <Button fullWidth variant="text" size="small" onClick={onClose} color="inherit">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main App ---
const NAVIGATION = [
  { kind: "header", title: "Main" },
  { segment: "dashboard", title: "ToDaaay", icon: <DashboardIcon /> },
  { segment: "all-tasks", title: "All Tasks", icon: <ListAltIcon /> },
];

export default function DashboardApp() {
  const initialPath = useMemo(() => {
    return localStorage.getItem("todaay_active_path") || "/dashboard";
  }, []);
  const router = useDemoRouter(initialPath);

  useEffect(() => {
    if (router?.pathname) {
      localStorage.setItem("todaay_active_path", router.pathname);
    }
  }, [router?.pathname]);

  const [tasks, setTasks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // ★設定状態
  const [calendarTimeMode, setCalendarTimeMode] = useState(() => {
    return localStorage.getItem("calendarTimeMode") || "daily";
  });
  const [focusHours, setFocusHours] = useState(() => {
    return Number(localStorage.getItem("focusHours")) || 4;
  });
  const [businessMinTime, setBusinessMinTime] = useState(() => {
    return localStorage.getItem("businessMinTime") || "09:00";
  });
  const [businessMaxTime, setBusinessMaxTime] = useState(() => {
    return localStorage.getItem("businessMaxTime") || "19:00";
  });
  const [dailyMinTime, setDailyMinTime] = useState(() => {
    return localStorage.getItem("dailyMinTime") || "07:00";
  });
  const [dailyMaxTime, setDailyMaxTime] = useState(() => {
    return localStorage.getItem("dailyMaxTime") || "23:00";
  });
  const [customMinTime, setCustomMinTime] = useState(() => {
    return localStorage.getItem("customMinTime") || "08:00";
  });
  const [customMaxTime, setCustomMaxTime] = useState(() => {
    return localStorage.getItem("customMaxTime") || "22:00";
  });
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(() => {
    const saved = localStorage.getItem("firstDayOfWeek");
    return saved !== null ? Number(saved) : 6;
  });
  const [hourlyChimeEnabled, setHourlyChimeEnabled] = useState(() => {
    const saved = localStorage.getItem("hourlyChimeEnabled");
    return saved !== null ? saved === "true" : true;
  });
  const [hourlyChimeInterval, setHourlyChimeInterval] = useState(() => {
    return Number(localStorage.getItem("hourlyChimeInterval")) || 60;
  });
  const [chimeMessage, setChimeMessage] = useState(null);

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ★高精度時報タイマー（毎秒正確に検知、スリープ復帰・バックグラウンド完全対応）
  useEffect(() => {
    if (!hourlyChimeEnabled) return;

    let lastChimedMinute = null;

    const intervalId = setInterval(() => {
      const now = new Date();
      const currentMin = now.getMinutes();
      const currentSec = now.getSeconds();
      const interval = Number(hourlyChimeInterval) || 60;

      // 指定した間隔の正分（:00秒付近）に到達したか判定
      const isTargetMinute = currentMin % interval === 0;
      const currentMinuteKey = `${now.getHours()}:${currentMin}`;

      if (isTargetMinute && currentSec === 0 && lastChimedMinute !== currentMinuteKey) {
        lastChimedMinute = currentMinuteKey;
        const hh = String(now.getHours()).padStart(2, "0");
        const mm = String(currentMin).padStart(2, "0");
        setChimeMessage(`現在 ${hh}:${mm} です`);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [hourlyChimeEnabled, hourlyChimeInterval]);

  useEffect(() => {
    if (!chimeMessage) return;
    const timer = setTimeout(() => {
      setChimeMessage(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [chimeMessage]);

  const triggerTestChime = () => {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    setChimeMessage(`現在 ${hh}:${mm} です`);
  };

  const timeSlots = useMemo(() => {
    if (calendarTimeMode === "business") {
      const minStr = businessMinTime ? `${businessMinTime}:00` : "09:00:00";
      const maxStr = businessMaxTime ? `${businessMaxTime}:00` : "19:00:00";
      return { min: minStr, max: maxStr };
    }
    if (calendarTimeMode === "daily") {
      const minStr = dailyMinTime ? `${dailyMinTime}:00` : "07:00:00";
      const maxStr = dailyMaxTime ? `${dailyMaxTime}:00` : "23:00:00";
      return { min: minStr, max: maxStr };
    }
    if (calendarTimeMode === "all") {
      return { min: "00:00:00", max: "24:00:00" };
    }
    if (calendarTimeMode === "custom") {
      const minStr = customMinTime ? `${customMinTime}:00` : "08:00:00";
      const maxStr = customMaxTime ? `${customMaxTime}:00` : "22:00:00";
      return { min: minStr, max: maxStr };
    }
    if (calendarTimeMode === "focus") {
      const currentHour = new Date().getHours();
      const h = Number(focusHours) || 4;
      const minH = Math.max(0, currentHour - h);
      const maxH = Math.min(24, currentHour + h);
      const minStr = `${String(minH).padStart(2, "0")}:00:00`;
      const maxStr = `${String(maxH).padStart(2, "0")}:00:00`;
      return { min: minStr, max: maxStr };
    }
    return { min: "00:00:00", max: "24:00:00" };
  }, [
    calendarTimeMode,
    businessMinTime,
    businessMaxTime,
    dailyMinTime,
    dailyMaxTime,
    focusHours,
    customMinTime,
    customMaxTime,
  ]);

  // ★テーマモード（"light" | "dark" | "system"）
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem("todaay_theme_mode") || "system";
  });
  const [systemIsDark, setSystemIsDark] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  // ★登録カテゴリ一覧（ユーザーが設定・追加可能）
  const [registeredCategories, setRegisteredCategories] = useState(() => {
    try {
      const saved = localStorage.getItem("todaay_registered_categories");
      return saved ? JSON.parse(saved) : ["仕事", "プライベート"];
    } catch (e) {
      return ["仕事", "プライベート"];
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "todaay_registered_categories",
      JSON.stringify(registeredCategories),
    );
  }, [registeredCategories]);

  // ★全タスクと登録リストから利用可能なカテゴリ一覧を動的に集約
  const availableCategories = useMemo(() => {
    const set = new Set();
    if (Array.isArray(registeredCategories)) {
      registeredCategories.forEach((c) => {
        if (c && typeof c === "string" && c.trim()) set.add(c.trim());
      });
    }
    tasks.forEach((t) => {
      if (t.category && typeof t.category === "string" && t.category.trim()) {
        set.add(t.category.trim());
      }
    });
    return Array.from(set);
  }, [tasks, registeredCategories]);

  // ★選択中カテゴリ（"all" または 任意のカテゴリ名）
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("todaay_selected_category") || "all";
  });

  useEffect(() => {
    localStorage.setItem("todaay_selected_category", selectedCategory);
  }, [selectedCategory]);

  // ★カテゴリが2つ未満（タブ非表示）または選択中カテゴリが存在しなくなった場合は "all" に自動同期
  useEffect(() => {
    if (
      !availableCategories ||
      availableCategories.length < 2 ||
      (selectedCategory !== "all" && !availableCategories.includes(selectedCategory))
    ) {
      if (selectedCategory !== "all") {
        setSelectedCategory("all");
      }
    }
  }, [availableCategories, selectedCategory]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemIsDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const effectiveDarkMode =
    themeMode === "dark" || (themeMode === "system" && systemIsDark);

  // ★DOMのdata-theme属性とクラスを更新（CSS変数およびスタイル連動）
  useEffect(() => {
    localStorage.setItem("todaay_theme_mode", themeMode);
    if (effectiveDarkMode) {
      document.documentElement.setAttribute("data-theme", "dark");
      document.documentElement.setAttribute("data-mui-color-scheme", "dark");
      document.body.classList.add("dark-mode", "dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
      document.documentElement.setAttribute("data-mui-color-scheme", "light");
      document.body.classList.remove("dark-mode", "dark");
    }
  }, [themeMode, effectiveDarkMode]);

  // ★MUIテーマ設定（Linear / Raycast風の洗練されたダーク/ライトテーマ）
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: effectiveDarkMode ? "dark" : "light",
        primary: {
          main: effectiveDarkMode ? "#60a5fa" : "#2563eb",
        },
        background: {
          default: effectiveDarkMode ? "#09090b" : "#f8fafc",
          paper: effectiveDarkMode ? "#18181b" : "#ffffff",
        },
        text: {
          primary: effectiveDarkMode ? "#f4f4f5" : "#0f172a",
          secondary: effectiveDarkMode ? "#a1a1aa" : "#64748b",
        },
        divider: effectiveDarkMode
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.06)",
      },
      shape: {
        borderRadius: 8,
      },
      typography: {
        fontFamily: [
          "-apple-system",
          "BlinkMacSystemFont",
          '"Segoe UI"',
          "Roboto",
          '"Helvetica Neue"',
          "Arial",
          "sans-serif",
        ].join(","),
      },
    });
  }, [effectiveDarkMode]);

  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState("free"); // "free" | "pro"
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [migrateSnackbar, setMigrateSnackbar] = useState(null); // null | "migrating" | "done" | "empty"

  // ★決済成功URLの検知（Stripeから戻ってきた時）
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("checkout") === "success") {
        setCheckoutSuccess(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // ★Firestoreからリアルタイムにユーザーのplanを購読
  useEffect(() => {
    if (!user) {
      setUserPlan("free");
      return;
    }
    const userDocRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setUserPlan(data.plan || "free");
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    let prevUser = null;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const justLoggedIn = !prevUser && currentUser;
      prevUser = currentUser;
      setUser(currentUser);
      setLoadingAuth(false);

      if (justLoggedIn) {
        // ★Firestoreから設定を読み込んでstateに反映
        try {
          const { doc, getDoc } = await import("firebase/firestore");
          const snap = await getDoc(doc(db, "users", currentUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            if (data.plan) setUserPlan(data.plan);
            const s = data.settings || {};
            if (s.themeMode !== undefined) setThemeMode(s.themeMode);
            if (s.registeredCategories !== undefined && Array.isArray(s.registeredCategories)) {
              setRegisteredCategories(s.registeredCategories);
            }
            if (s.calendarTimeMode !== undefined) setCalendarTimeMode(s.calendarTimeMode);
            if (s.focusHours !== undefined) setFocusHours(Number(s.focusHours));
            if (s.businessMinTime !== undefined) setBusinessMinTime(s.businessMinTime);
            if (s.businessMaxTime !== undefined) setBusinessMaxTime(s.businessMaxTime);
            if (s.dailyMinTime !== undefined) setDailyMinTime(s.dailyMinTime);
            if (s.dailyMaxTime !== undefined) setDailyMaxTime(s.dailyMaxTime);
            if (s.customMinTime !== undefined) setCustomMinTime(s.customMinTime);
            if (s.customMaxTime !== undefined) setCustomMaxTime(s.customMaxTime);
            if (s.firstDayOfWeek !== undefined) setFirstDayOfWeek(Number(s.firstDayOfWeek));
            if (s.hourlyChimeEnabled !== undefined) setHourlyChimeEnabled(Boolean(s.hourlyChimeEnabled));
            if (s.hourlyChimeInterval !== undefined) setHourlyChimeInterval(Number(s.hourlyChimeInterval));
          }
        } catch (e) {
          console.error("Settings load failed:", e);
        }


        // ★ローカルのタスクをFirestoreへ移行
        try {
          const saved = localStorage.getItem("todaay_local_tasks");
          const localTasks = saved ? JSON.parse(saved) : [];
          if (localTasks.length > 0) {
            setMigrateSnackbar("migrating");
            const { addDoc, collection, serverTimestamp } = await import("firebase/firestore");
            await Promise.all(
              localTasks.map((task) => {
                const { id: _id, created: _created, ...rest } = task;
                return addDoc(collection(db, "tasks"), {
                  ...rest,
                  created: serverTimestamp(),
                  uid: currentUser.uid,
                });
              })
            );
            localStorage.removeItem("todaay_local_tasks");
            setMigrateSnackbar("done");
          }
        } catch (e) {
          console.error("Migration failed:", e);
        }
      }

      // ★設定の書き込みを許可（未ログイン時も即OK）
      setSettingsLoaded(true);
    });
    return () => unsubscribe();
  }, []);


  // ★設定を一括保存（ログイン済み→Firestore / 未ログイン→localStorage）
  // ※ user の宣言より後に配置しないと "Cannot access 'user' before initialization" になる
  useEffect(() => {
    if (!settingsLoaded) return; // Firestoreからの読み込み完了前は書き込まない

    const settings = {
      themeMode,
      registeredCategories,
      calendarTimeMode,
      focusHours,
      businessMinTime,
      businessMaxTime,
      dailyMinTime,
      dailyMaxTime,
      customMinTime,
      customMaxTime,
      firstDayOfWeek,
      hourlyChimeEnabled,
      hourlyChimeInterval,
    };

    if (user) {
      // ★ログイン済み → Firestore に保存
      import("firebase/firestore").then(({ doc, setDoc }) => {
        setDoc(doc(db, "users", user.uid), { settings }, { merge: true }).catch(
          (e) => console.error("Settings save failed:", e)
        );
      });
    } else {
      // ★未ログイン → localStorage に保存
      localStorage.setItem("todaay_theme_mode", themeMode);
      localStorage.setItem("todaay_registered_categories", JSON.stringify(registeredCategories));
      localStorage.setItem("calendarTimeMode", calendarTimeMode);
      localStorage.setItem("focusHours", focusHours);
      localStorage.setItem("businessMinTime", businessMinTime);
      localStorage.setItem("businessMaxTime", businessMaxTime);
      localStorage.setItem("dailyMinTime", dailyMinTime);
      localStorage.setItem("dailyMaxTime", dailyMaxTime);
      localStorage.setItem("customMinTime", customMinTime);
      localStorage.setItem("customMaxTime", customMaxTime);
      localStorage.setItem("firstDayOfWeek", firstDayOfWeek);
      localStorage.setItem("hourlyChimeEnabled", hourlyChimeEnabled);
      localStorage.setItem("hourlyChimeInterval", hourlyChimeInterval);
    }
  }, [
    user,
    settingsLoaded,
    themeMode,
    registeredCategories,
    calendarTimeMode,
    focusHours,
    businessMinTime,
    businessMaxTime,
    dailyMinTime,
    dailyMaxTime,
    customMinTime,
    customMaxTime,
    firstDayOfWeek,
    hourlyChimeEnabled,
    hourlyChimeInterval,
  ]);

  // ★ローカルストレージからタスクを読み込む（未ログイン時）

  const loadLocalTasks = () => {
    try {
      const saved = localStorage.getItem("todaay_local_tasks");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  };

  const saveLocalTasks = (taskList) => {
    try {
      localStorage.setItem("todaay_local_tasks", JSON.stringify(taskList));
    } catch (e) {}
  };

  useEffect(() => {
    if (user) {
      // ★ログイン済み → Firestore をリアルタイム購読
      const q = query(
        collection(db, "tasks"),
        where("uid", "==", user.uid),
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setTasks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsubscribe();
    } else {
      // ★未ログイン → localStorage から読み込み
      setTasks(loadLocalTasks());
    }
  }, [user]);

  useEffect(() => {
    // 1. ローカルキャッシュがあれば即時適用（0ms）
    const cached = localStorage.getItem("jp_holidays_cache");
    const cachedTime = localStorage.getItem("jp_holidays_cache_time");
    const isCacheValid =
      cached &&
      cachedTime &&
      Date.now() - Number(cachedTime) < 7 * 24 * 60 * 60 * 1000;

    if (cached) {
      try {
        setHolidays(JSON.parse(cached));
      } catch (e) {}
    }

    // 2. キャッシュがない、または1週間以上経過していればバックグラウンド取得
    if (!isCacheValid) {
      fetch("https://holidays-jp.github.io/api/v1/date.json")
        .then((res) => res.json())
        .then((data) => {
          const list = Object.keys(data).map((date) => ({
            id: `holiday-${date}`,
            title: data[date],
            start: date,
            allDay: true,
            editable: false,
            color: "#ffebee",
            textColor: "#c62828",
            borderColor: "transparent",
          }));
          setHolidays(list);
          try {
            localStorage.setItem("jp_holidays_cache", JSON.stringify(list));
            localStorage.setItem("jp_holidays_cache_time", String(Date.now()));
          } catch (e) {}
        })
        .catch(() => {});
    }
  }, []);

  const FREE_TASK_LIMIT = 50;

  const addTask = async (data) => {
    // ★PROプランでない場合は50件制限
    if (userPlan !== "pro" && tasks.length >= FREE_TASK_LIMIT) {
      setIsPricingOpen(true);
      return;
    }

    if (user) {
      // ★ログイン済み → Firestore
      await addDoc(collection(db, "tasks"), {
        ...data,
        created: serverTimestamp(),
        uid: user.uid,
      });
    } else {
      // ★未ログイン → localStorage
      const current = loadLocalTasks();
      const newTask = {
        ...data,
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        created: new Date().toISOString(),
      };
      const updated = [...current, newTask];
      saveLocalTasks(updated);
      setTasks(updated);
    }
  };

  const editTask = async (id, data) => {
    if (user) {
      // ★ログイン済み → Firestore
      await updateDoc(doc(db, "tasks", id), data);
    } else {
      // ★未ログイン → localStorage
      const current = loadLocalTasks();
      const updated = current.map((t) => (t.id === id ? { ...t, ...data } : t));
      saveLocalTasks(updated);
      setTasks(updated);
    }
  };

  // ★タスク削除のUndoトースト状態
  const [deleteUndoState, setDeleteUndoState] = useState({
    open: false,
    task: null,
  });

  const handleUndoDelete = async () => {
    if (!deleteUndoState.task) return;
    const taskToRestore = deleteUndoState.task;
    setDeleteUndoState({ open: false, task: null });

    const { id: _id, ...taskData } = taskToRestore;
    await addTask(taskData);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        if (deleteUndoState.open) {
          e.preventDefault();
          handleUndoDelete();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteUndoState]);

  const deleteTask = async (id) => {
    const targetTask = tasks.find((t) => t.id === id);
    if (targetTask) {
      setDeleteUndoState({
        open: true,
        task: targetTask,
      });
    }

    if (user) {
      // ★ログイン済み → Firestore
      await deleteDoc(doc(db, "tasks", id));
    } else {
      // ★未ログイン → localStorage
      const current = loadLocalTasks();
      const updated = current.filter((t) => t.id !== id);
      saveLocalTasks(updated);
      setTasks(updated);
    }
  };

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme} // ★テーマ適用
      branding={{ logo: "", title: "ToDaay" }}
    >
      <DashboardLayout
        defaultSidebarCollapsed={true}
        slots={{
          sidebarFooter: ({ mini }) => {
            const isSelected = router.pathname === "/settings";
            return (
              <Box
                sx={{
                  p: mini ? 0.8 : 1.2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {/* ★PROアップグレード案内（PROでない場合のみ） */}
                {userPlan !== "pro" && (
                  <Tooltip title="Proプランにアップグレード" placement="right" disableHoverListener={!mini}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setIsPricingOpen(true)}
                      sx={{
                        justifyContent: mini ? "center" : "flex-start",
                        minWidth: 0,
                        px: mini ? 1 : 1.8,
                        py: 0.8,
                        borderRadius: 2,
                        background: "linear-gradient(45deg, #2563eb, #3b82f6)",
                        color: "#fff",
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "0.8rem",
                        boxShadow: "none",
                      }}
                    >
                      <BoltIcon sx={{ mr: mini ? 0 : 1, fontSize: 18, color: "#fde047" }} />
                      {!mini && "Proにアップグレード"}
                    </Button>
                  </Tooltip>
                )}

                <Tooltip
                  title="設定"
                  placement="right"
                  disableHoverListener={!mini}
                >
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => router.navigate("/settings")}
                    sx={{
                      justifyContent: mini ? "center" : "flex-start",
                      minWidth: 0,
                      px: mini ? 1 : 1.8,
                      py: 1,
                      borderRadius: 2,
                      color: isSelected ? "primary.main" : "text.primary",
                      bgcolor: isSelected ? "action.selected" : "transparent",
                      fontWeight: isSelected ? 700 : 500,
                      textTransform: "none",
                      "&:hover": {
                        bgcolor: isSelected
                          ? "action.selected"
                          : "action.hover",
                      },
                    }}
                  >
                    <SettingsIcon
                      sx={{
                        mr: mini ? 0 : 1.5,
                        fontSize: 22,
                        color: isSelected ? "primary.main" : "text.secondary",
                      }}
                    />
                    {!mini && (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "inherit" }}
                      >
                        設定
                      </Typography>
                    )}
                  </Button>
                </Tooltip>
              </Box>
            );
          },
          toolbarActions: () => (
            <Stack direction="row" alignItems="center" spacing={1.5} mr={1}>
              <HeaderClock />
              {/* ★テーマモード切り替えボタン（1タップでライト/ダーク切り替え） */}
              <ThemeModeToggle
                themeMode={themeMode}
                setThemeMode={setThemeMode}
                effectiveDarkMode={effectiveDarkMode}
              />

              {userPlan === "pro" ? (
                <Chip
                  icon={<BoltIcon sx={{ fontSize: "16px !important", color: "#eab308 !important" }} />}
                  label="PRO"
                  size="small"
                  onClick={() => setIsPricingOpen(true)}
                  sx={{
                    fontWeight: "800",
                    fontSize: "0.75rem",
                    bgcolor: "rgba(234, 179, 8, 0.12)",
                    color: "#ca8a04",
                    border: "1px solid rgba(234, 179, 8, 0.3)",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<BoltIcon sx={{ color: "#eab308" }} />}
                  onClick={() => setIsPricingOpen(true)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    color: "primary.main",
                  }}
                >
                  Pro
                </Button>
              )}
              {user ? (
                <>
                  <Avatar
                    src={user.photoURL}
                    alt={user.displayName}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Tooltip title="ログアウト">
                    <IconButton onClick={handleLogout} color="inherit" size="small">
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="ログインするとデータがクラウドに同期されます">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LoginIcon />}
                    onClick={handleLogin}
                    sx={{ fontWeight: "bold", textTransform: "none" }}
                  >
                    ログイン
                  </Button>
                </Tooltip>
              )}
            </Stack>
          ),
        }}
      >
        <Box
          key={router.pathname}
          className="page-fade-in"
          sx={{
            height: "calc(100svh - 64px)",
            overflowY: "auto",
            "@media (max-width: 600px)": {
              height: "calc(100svh - 56px)",
            },
          }}
        >
          {(router.pathname === "/dashboard" || router.pathname === "/") && (
            <ToDaaayPage
              tasks={tasks}
              holidays={holidays}
              addTask={addTask}
              editTask={editTask}
              deleteTask={deleteTask}
              setEditingTask={setEditingTask}
              setIsModalOpen={setIsModalOpen}
              timeSlots={timeSlots}
              firstDayOfWeek={firstDayOfWeek}
              calendarTimeMode={calendarTimeMode}
              setCalendarTimeMode={setCalendarTimeMode}
              focusHours={focusHours}
              businessMinTime={businessMinTime}
              businessMaxTime={businessMaxTime}
              dailyMinTime={dailyMinTime}
              dailyMaxTime={dailyMaxTime}
              customMinTime={customMinTime}
              customMaxTime={customMaxTime}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              availableCategories={availableCategories}
              userPlan={userPlan}
              onOpenPricing={() => setIsPricingOpen(true)}
            />
          )}
          {router.pathname === "/all-tasks" && (
            <AllTasksPage
              tasks={tasks}
              setEditingTask={setEditingTask}
              setIsModalOpen={setIsModalOpen}
              editTask={editTask}
              firstDayOfWeek={firstDayOfWeek}
            />
          )}
          {router.pathname === "/settings" && (
            <SettingsPage
              calendarTimeMode={calendarTimeMode}
              setCalendarTimeMode={setCalendarTimeMode}
              focusHours={focusHours}
              setFocusHours={setFocusHours}
              businessMinTime={businessMinTime}
              setBusinessMinTime={setBusinessMinTime}
              businessMaxTime={businessMaxTime}
              setBusinessMaxTime={setBusinessMaxTime}
              dailyMinTime={dailyMinTime}
              setDailyMinTime={setDailyMinTime}
              dailyMaxTime={dailyMaxTime}
              setDailyMaxTime={setDailyMaxTime}
              customMinTime={customMinTime}
              setCustomMinTime={setCustomMinTime}
              customMaxTime={customMaxTime}
              setCustomMaxTime={setCustomMaxTime}
              firstDayOfWeek={firstDayOfWeek}
              setFirstDayOfWeek={setFirstDayOfWeek}
              hourlyChimeEnabled={hourlyChimeEnabled}
              setHourlyChimeEnabled={setHourlyChimeEnabled}
              hourlyChimeInterval={hourlyChimeInterval}
              setHourlyChimeInterval={setHourlyChimeInterval}
              triggerTestChime={triggerTestChime}
              themeMode={themeMode}
              setThemeMode={setThemeMode}
              registeredCategories={registeredCategories}
              setRegisteredCategories={setRegisteredCategories}
              tasks={tasks}
              editTask={editTask}
              deleteTask={deleteTask}
            />
          )}
        </Box>
        <TaskModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
          defaultValues={editingTask || {}}
          onSubmit={(data) => {
            if (editingTask && editingTask.id) editTask(editingTask.id, data);
            else addTask(data);
          }}
          onDelete={deleteTask}
        />
        <TaskPromptToast tasks={tasks} editTask={editTask} />
        <TimeChimeToast
          message={chimeMessage}
          onClose={() => setChimeMessage(null)}
        />
        {/* ★ローカルデータ移行スナックバー */}
        <Snackbar
          open={migrateSnackbar === "migrating"}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message="☁️ ローカルのタスクをクラウドに移行中..."
        />
        {/* ★決済完了スナックバー */}
        <Snackbar
          open={checkoutSuccess}
          autoHideDuration={6000}
          onClose={() => setCheckoutSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          message="🎉 Todaaay Pro へのアップグレードが完了しました！"
        />
        {/* ★タスク削除 Undo スナックバー */}
        <Snackbar
          open={deleteUndoState.open}
          autoHideDuration={6000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;
            setDeleteUndoState((prev) => ({ ...prev, open: false }));
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          message={`「${deleteUndoState.task?.title || "タスク"}」を削除しました`}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={handleUndoDelete}
                startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: "14px",
                  px: 1.5,
                  py: 0.3,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                元に戻す
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() =>
                  setDeleteUndoState((prev) => ({ ...prev, open: false }))
                }
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
          sx={{
            "& .MuiSnackbarContent-root": {
              bgcolor: "#1e293b",
              color: "#f8fafc",
              borderRadius: 3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.1)",
            },
          }}
        />

        {/* ★プランモーダル */}
        <PricingModal
          open={isPricingOpen}
          onClose={() => setIsPricingOpen(false)}
          user={user}
          onLogin={handleLogin}
          userPlan={userPlan}
        />
      </DashboardLayout>
    </AppProvider>
  );
}
